/**
 * Analytics contracts (WP3-ANALYTICS, BUILD-PLAN M17). Imported as
 * `@velora/contracts/analytics`.
 *
 * Every number comes from the Metric Dictionary (`@velora/domain/metrics`):
 * the API measures additive components per office, merges them for "All
 * offices" (sums, set unions, ratio of summed parts) and only then derives
 * the displayed value, so an office row and the total row can never
 * disagree. Every tile can be drilled to the rows behind it, and the rows
 * add up to the tile.
 *
 * Routes under `/v1/analytics`:
 * - `GET  /dictionary`               the Metric Dictionary and presets
 * - `GET  /board`                    performance board (tiles, office comparison, pace, forecast, funnel, hygiene, new patients, collections lag)
 * - `GET  /metrics/:metricId/rows`   drill-down rows for one tile (audited PHI read)
 * - `GET  /huddle`                   morning huddle for yesterday, today or the next working day
 * - `GET  /providers`                provider scorecards with a per-office split
 * - `GET  /finder`                   Patient Finder segments (audited PHI read)
 * - `POST /finder/tasks`             follow-up tasks for selected patients
 * - `GET  /reports/day-sheet`        day sheet (procedures, payments, adjustments)
 * - `GET  /reports/production`       production and collections by office, provider, day or month
 * - `GET  /reports/aging`            accounts receivable aging by guarantor (paged)
 * - `GET  /reports/referrals`        new patients by referral source
 * - `GET  /outliers`                 fee outliers for Data hygiene (audited PHI read)
 * - `POST /outliers/:procedureId/ack` acknowledge a fee outlier (audited)
 * - `GET  /finder/export`            the whole Finder segment as CSV (audited PHI read)
 * - `GET  /goals`, `PUT /goals`      monthly production and new-patient goals per office
 *
 * Data availability (V08): a metric whose data feed the office's PMS link
 * does not sync is `null` with `unavailable: { feed, reason }` (never $0 or
 * 0%), and totals cover only the offices that could be measured
 * (`coverage`, "N of M offices").
 *
 * Money: users without `money.view` in an office get `null` for every cent
 * amount of that office (and of any total that includes it). Ratios and
 * counts stay visible (analytics-huddle AH-12).
 */
import { z } from 'zod';
import { AcceptanceFiguresSchema } from './txplan.js';

const IsoDateSchema = z.string().regex(/^\d{4}-\d{2}-\d{2}$/, 'expected YYYY-MM-DD');
const MonthSchema = z.string().regex(/^\d{4}-(0[1-9]|1[0-2])$/, 'expected YYYY-MM');

/* ------------------------------------------------------------------------ */
/* Shared                                                                    */
/* ------------------------------------------------------------------------ */

export const METRIC_PRESETS = ['velora', 'di_compatible', 'pbn_compatible'] as const;
export const MetricPresetSchema = z.enum(METRIC_PRESETS);
export type MetricPresetKey = z.infer<typeof MetricPresetSchema>;

export const PRESET_LABELS: Record<MetricPresetKey, string> = {
  velora: 'Velora default',
  di_compatible: 'Dental Intelligence compatible',
  pbn_compatible: 'Practice by Numbers compatible',
};

/** Board periods. All end on the practice-local `asOf` date (today unless `date` is given). */
export const BOARD_PERIODS = ['mtd', 'last_month', 'qtd', '90d', 'ytd', '12m'] as const;
export const BoardPeriodSchema = z.enum(BOARD_PERIODS);
export type BoardPeriod = z.infer<typeof BoardPeriodSchema>;

/** Pivot scorecard dimensions (see PIVOT_DIMENSION_INFO). */
export const PIVOT_DIMENSIONS = [
  'office',
  'provider',
  'role',
  'procedure_category',
  'procedure_code',
  'referral_source',
  'carrier',
  'team_member',
  'appt_weekday',
  'appt_hour',
  'booking_lead',
  // Roadmap Tier 2/3: attributes of fact rows (staff, sources, bands ...).
  'staff_member',
  'lead_source',
  'demand_reason',
  'price_band',
  'payer_type',
  'followup_step',
  'claim_carrier',
  'denial_reason',
  'payment_type',
] as const;
export const PivotDimensionSchema = z.enum(PIVOT_DIMENSIONS);
export type PivotDimension = z.infer<typeof PivotDimensionSchema>;

export const BOARD_PERIOD_LABELS: Record<BoardPeriod, string> = {
  mtd: 'Month to date',
  last_month: 'Last month',
  qtd: 'Quarter to date',
  '90d': 'Last 90 days',
  ytd: 'Year to date',
  '12m': 'Last 12 months',
};

export const COMPARE_MODES = ['prior', 'year'] as const;
export const CompareModeSchema = z.enum(COMPARE_MODES);
export type CompareMode = z.infer<typeof CompareModeSchema>;

export const ResolvedPeriodSchema = z.object({
  start: IsoDateSchema,
  end: IsoDateSchema,
  /** Evaluation date: nothing after it counts. */
  asOf: IsoDateSchema,
  label: z.string(),
});
export type ResolvedPeriod = z.infer<typeof ResolvedPeriodSchema>;

export const MetricUnitSchema = z.enum([
  'cents',
  'count',
  'ratio',
  'cents_per_hour',
  'cents_per_visit',
  'cents_per_patient',
  'cents_per_day',
  'minutes',
  'days',
  'rating',
]);
export type MetricUnitKey = z.infer<typeof MetricUnitSchema>;

export const RollupRuleSchema = z.enum(['sum', 'distinct_count', 'ratio_of_sums', 'snapshot_sum']);

/** Why a number is missing or partial: the data feed and a plain-language reason. */
export const MetricGapSchema = z.object({ feed: z.string(), reason: z.string() });
export type MetricGap = z.infer<typeof MetricGapSchema>;

export const OfficeGapSchema = MetricGapSchema.extend({ locationId: z.uuid() });
export type OfficeGap = z.infer<typeof OfficeGapSchema>;

/** Offices a total covers ("N of M offices"); the rest are listed with the reason. */
export const CoverageSchema = z.object({
  offices: z.number().int(),
  available: z.array(z.uuid()),
  unavailable: z.array(OfficeGapSchema),
});
export type Coverage = z.infer<typeof CoverageSchema>;

/** One computed value. Cent components are `null` when money is hidden. */
export const MetricCellSchema = z.object({
  value: z.number().nullable(),
  components: z.record(z.string(), z.number().nullable()),
  /** Set when the office's PMS link does not sync a feed this metric needs (value is null). */
  unavailable: MetricGapSchema.optional(),
  /** Set when the value is computed from incomplete data (shown as partial or estimated). */
  partial: z.array(MetricGapSchema).optional(),
  /**
   * Set when a rate rests on fewer cases than its floor (`min_n`, WP-2): the
   * value is shown muted with "only n", never as a headline.
   */
  lowN: z.object({ n: z.number(), min: z.number(), unit: z.enum(['count', 'minutes']) }).optional(),
});
export type MetricCell = z.infer<typeof MetricCellSchema>;

export const ProviderRefSchema = z.object({
  id: z.uuid(),
  name: z.string(),
  abbr: z.string(),
  type: z.enum(['dentist', 'hygienist', 'assistant', 'other']),
  colorToken: z.string(),
});
export type ProviderRef = z.infer<typeof ProviderRefSchema>;

/** Office scope actually shown, for the "Showing 2 of 3 offices" banner (AM-10, AH-20). */
export const ScopeInfoSchema = z.object({
  /** Offices in the response, display order. */
  locationIds: z.array(z.uuid()),
  /** Offices the selector asked for that the user may see (before permission narrowing). */
  requested: z.number().int(),
  /** True when at least one office's money was withheld. */
  moneyHidden: z.boolean(),
  /**
   * Offices left out because they do not have the Marketplace module `key`
   * (e.g. analytics_pro). Owner role test: the banner blamed the role for a
   * missing add-on. Offices the role cannot open are not named here; they
   * are the rest of `requested - locationIds.length`.
   */
  excluded: z
    .array(
      z.object({
        locationId: z.uuid(),
        reason: z.enum(['feature', 'permission']),
        key: z.string(),
      }),
    )
    .default([]),
});
export type ScopeInfo = z.infer<typeof ScopeInfoSchema>;

/* ------------------------------------------------------------------------ */
/* Dictionary                                                                */
/* ------------------------------------------------------------------------ */

export const DictionaryQuerySchema = z.object({
  /** Omitted: the office's (else the practice's) default preset. */
  preset: MetricPresetSchema.optional(),
});

export const DictionaryComponentSchema = z.object({
  name: z.string(),
  kind: z.enum(['sum', 'distinct']),
  unit: z.enum(['cents', 'count', 'minutes']),
  description: z.string(),
});

export const DictionaryMetricSchema = z.object({
  id: z.string(),
  version: z.number().int(),
  name: z.string(),
  category: z.enum([
    'production',
    'collections',
    'treatment',
    'hygiene',
    'patients',
    'schedule',
    'ar',
    'recoverable',
    'front_desk',
    'marketing',
    'forecast',
  ]),
  unit: MetricUnitSchema,
  rollup: RollupRuleSchema,
  timeAggregation: z.enum(['flow', 'stock']),
  better: z.enum(['higher', 'lower', 'neutral']),
  attribution: z.string(),
  dimensions: z.array(z.enum(['location', 'provider'])),
  formula: z.string(),
  notes: z.array(z.string()),
  sources: z.array(z.string()),
  components: z.array(DictionaryComponentSchema),
  /** Data feeds the metric needs (V08); without one the value is unavailable. */
  requires: z.array(z.string()),
  /** Feeds whose absence makes the value partial or estimated. */
  partialWithout: z.array(z.string()),
  /** Ratio metrics: the numerator and denominator component names. */
  numerator: z.string().nullable(),
  denominator: z.string().nullable(),
  /** Sum metrics: the value component. */
  valueComponent: z.string().nullable(),
  /** True when the value contains money. */
  money: z.boolean(),
});
export type DictionaryMetric = z.infer<typeof DictionaryMetricSchema>;

export const DictionaryParamSchema = z.object({
  key: z.string(),
  label: z.string(),
  value: z.string(),
});

export const MetricDictionarySchema = z.object({
  preset: MetricPresetSchema,
  presets: z.array(z.object({ key: MetricPresetSchema, label: z.string() })),
  /** The preset's parameters in plain words (windows, code lists, bases). */
  params: z.array(DictionaryParamSchema),
  metrics: z.array(DictionaryMetricSchema),
});
export type MetricDictionary = z.infer<typeof MetricDictionarySchema>;

/* ------------------------------------------------------------------------ */
/* Board                                                                     */
/* ------------------------------------------------------------------------ */

/* ------------------------------------------------------------------------ */
/* Stored analytics results (snapshots)                                      */
/* ------------------------------------------------------------------------ */

/**
 * Where an analytics answer came from. The worker precomputes the board,
 * huddle, scorecard, schedule analysis and goal pacing per office set and
 * stores them (`analytics_snapshot`); the API answers from the stored result
 * in milliseconds and says how old it is. `stale`: older than the refresh
 * threshold or computed before a goal/setting change; a refresh is queued
 * and the client may ask again shortly. `source: 'live'`: computed in the
 * request (no job queue: tests, scripts).
 */
export const SnapshotInfoSchema = z.object({
  source: z.enum(['snapshot', 'live']),
  /** When the numbers were computed (ISO timestamp). */
  computedAt: z.string(),
  ageSeconds: z.number().int().min(0),
  stale: z.boolean(),
});
export type SnapshotInfo = z.infer<typeof SnapshotInfoSchema>;

/**
 * HTTP 202 from a snapshot route: nothing is stored yet for this office set
 * and query (first ever view, or a custom range); the worker is computing
 * it. Ask again after `retryAfterMs`.
 */
export const SnapshotPreparingSchema = z.object({
  preparing: z.literal(true),
  retryAfterMs: z.number().int().min(0),
});
export type SnapshotPreparing = z.infer<typeof SnapshotPreparingSchema>;

export const BoardQuerySchema = z.object({
  period: BoardPeriodSchema.default('mtd'),
  compare: CompareModeSchema.default('prior'),
  /** Omitted: the office's (else the practice's) default preset. */
  preset: MetricPresetSchema.optional(),
  /** Practice-local "today"; defaults to the practice's current date. */
  date: IsoDateSchema.optional(),
});
export type BoardQuery = z.input<typeof BoardQuerySchema>;

export const BoardOfficeValueSchema = MetricCellSchema.extend({
  locationId: z.uuid(),
  /** Same metric for the comparison period; `null` when n/a or hidden. */
  compare: z.number().nullable(),
});

export const BoardTileSchema = z.object({
  metricId: z.string(),
  name: z.string(),
  unit: MetricUnitSchema,
  rollup: RollupRuleSchema,
  better: z.enum(['higher', 'lower', 'neutral']),
  money: z.boolean(),
  total: MetricCellSchema.extend({ compare: z.number().nullable() }),
  offices: z.array(BoardOfficeValueSchema),
  /** Median office value for ratio metrics, labelled as a median (AH-18). */
  median: z.number().nullable(),
  /** False when offices add up to more than the total (a patient seen at two offices, AH-19). */
  groupsSumToTotal: z.boolean(),
  /** Trend of the total over the period in equal buckets (oldest first). */
  spark: z.array(z.number().nullable()),
  /** Offices the total covers (V08); offices without the needed feeds are listed. */
  coverage: CoverageSchema,
});
export type BoardTile = z.infer<typeof BoardTileSchema>;

export const PaceSchema = z.object({
  days: z.array(IsoDateSchema),
  /** Cumulative gross production per day, cents. */
  production: z.array(z.number().int()).nullable(),
  /** Cumulative collections per day, cents. */
  collections: z.array(z.number().int()).nullable(),
  /** Cumulative goal pace per day (goal spread over working days), cents. */
  goal: z.array(z.number().int()).nullable(),
  /**
   * Cumulative gross production of the comparison period (prior period or
   * the same period last year), by day offset from its start, cents. Lets the
   * pace chart draw "last year" beside this period. Absent in older snapshots.
   */
  compareProduction: z.array(z.number().int()).nullable().optional(),
  /** Each office's goal pace on the last day (its goal x working days done / working days). */
  officeGoalToDate: z.array(z.object({ locationId: z.uuid(), cents: z.number().int().nullable() })),
  /** Set when no office in view syncs payments (the collections line is omitted, not $0). */
  collectionsUnavailable: MetricGapSchema.nullable(),
  /** Unacknowledged fee outliers left out of the production line (V08 outlier guard). */
  excludedOutliers: z.object({ count: z.number().int(), cents: z.number().int().nullable() }),
});
export type Pace = z.infer<typeof PaceSchema>;

export const ForecastOfficeSchema = z.object({
  locationId: z.uuid(),
  completedCents: z.number().int().nullable(),
  /** Fees of procedures attached to still-scheduled appointments, rest of the month. */
  scheduledCents: z.number().int().nullable(),
  /** Why scheduled production is unknown (Dentrix v1: procedures not linked to appointments). */
  scheduledUnavailable: MetricGapSchema.nullable(),
  /** Scheduled appointments in the rest of the month with no priced procedure (AH-07); `null` when unknowable. */
  unpricedAppointments: z.number().int().nullable(),
  goalCents: z.number().int().nullable(),
  /** Working days left after today, from the office's provider schedules (else its appointment days). */
  workdaysLeft: z.number().int(),
  /**
   * Where working days come from (HA-07): provider schedules; else the days
   * that have appointments; `none` = the forecast needs provider schedules.
   */
  workdaysSource: z.enum(['provider_schedules', 'appointments', 'none']),
  /** Goal minus completed minus scheduled, floored at 0; `null` without a goal. */
  scheduleGapCents: z.number().int().nullable(),
  /** Scheduled production expected to break, from the office's 90-day break rate. */
  completionGapCents: z.number().int().nullable(),
  breakRate: z.number().nullable(),
  /** (Goal - completed) / working days left (today included when still open). */
  needPerDayCents: z.number().int().nullable(),
  projectedCents: z.number().int().nullable(),
});
export type ForecastOffice = z.infer<typeof ForecastOfficeSchema>;

export const ForecastSchema = z.object({
  month: MonthSchema,
  offices: z.array(ForecastOfficeSchema),
  /** Sums of office values; need per day is the sum of each office's own need (AH-21). */
  total: ForecastOfficeSchema.omit({ locationId: true, breakRate: true }).extend({
    breakRate: z.number().nullable(),
    /**
     * Some offices in view have a monthly goal and some do not (owner
     * 2026-10-20). `goalCents` is then null (a part goal against the whole
     * total would read "526% of goal"); this says "goal set for 3 of 5
     * offices" and gives those offices' goal and production, so they are
     * compared like with like. Null when every office or none has a goal.
     */
    goalCoverage: z
      .object({
        offices: z.number().int(),
        of: z.number().int(),
        goalCents: z.number().int(),
        completedCents: z.number().int().nullable(),
        scheduledCents: z.number().int().nullable(),
        projectedCents: z.number().int().nullable(),
      })
      .nullable()
      .default(null),
  }),
  /** Unacknowledged fee outliers left out of completed production. */
  excludedOutlierCents: z.number().int().nullable(),
});
export type Forecast = z.infer<typeof ForecastSchema>;

export const FunnelStageSchema = z.object({
  /** Presented; scheduled or done (accepted); completed. Each stage includes the later ones. */
  key: z.enum(['presented', 'accepted', 'completed']),
  label: z.string(),
  cents: z.number().int().nullable(),
  /** Patients with at least this stage. */
  patients: z.number().int(),
});

export const FunnelSchema = z.object({
  stages: z.array(FunnelStageSchema),
  /** Presented but neither scheduled nor done as of the period's as-of date (the leakage). */
  notAccepted: z.object({ patients: z.number().int(), cents: z.number().int().nullable() }),
  /** No office in view links procedures to appointments: acceptance is unknown. */
  unavailable: MetricGapSchema.nullable(),
  coverage: CoverageSchema,
});
export type Funnel = z.infer<typeof FunnelSchema>;

export const HygieneProviderSchema = z.object({
  providerId: z.string(),
  visits: z.number().int(),
  reappointed: z.number().int(),
  rate: z.number().nullable(),
});

export const HygieneSummarySchema = z.object({
  visits: z.number().int(),
  reappointed: z.number().int(),
  rate: z.number().nullable(),
  providers: z.array(HygieneProviderSchema),
  recallDue: z.number().int(),
  recallBooked: z.number().int(),
  recallRate: z.number().nullable(),
  /** Hygiene visits before Velora observed the office (booking date unknown), left out. */
  notEvaluable: z.number().int(),
  /** The next hygiene visit was recognised by provider or reason, not codes (estimate). */
  estimated: z.boolean(),
  /** Why re-appointment cannot be shown for any office in view. */
  unavailable: MetricGapSchema.nullable(),
  recallUnavailable: MetricGapSchema.nullable(),
  /** Recall types in use that are bare PMS numbers and are not counted (RC-03). */
  unmappedRecallTypes: z.number().int(),
});

export const NewPatientsSummarySchema = z.object({
  /** Trailing 12 months, oldest first, ending with the as-of month. */
  months: z.array(z.object({ month: MonthSchema, count: z.number().int() })),
  /** Referral sources of this period's new patients. */
  sources: z.array(z.object({ label: z.string(), count: z.number().int() })),
  /** First-visit dates are not synced for any office in view. */
  unavailable: MetricGapSchema.nullable(),
  coverage: CoverageSchema,
});

export const LagRowSchema = z.object({
  month: MonthSchema,
  /** Net production of procedures completed in the month. */
  netCents: z.number().int(),
  /** Cumulative share collected by month offset 0..5 (`null` for offsets in the future). */
  cumulative: z.array(z.number().nullable()),
  collectedCents: z.number().int(),
  owedCents: z.number().int(),
  open: z.boolean(),
});

export const CollectionsLagSchema = z.object({ rows: z.array(LagRowSchema) });
export type CollectionsLag = z.infer<typeof CollectionsLagSchema>;

export const OfficeGoalSchema = z.object({
  locationId: z.uuid(),
  month: MonthSchema,
  productionGoalCents: z.number().int().min(0).nullable(),
  newPatientGoal: z.number().int().min(0).nullable(),
  updatedAt: z.string().nullable(),
  /**
   * Where the goal came from: entered for the office (v1 goal or an office
   * target), its share of a practice goal, or none.
   */
  source: z.enum(['office', 'practice_share', 'none']).optional(),
});
export type OfficeGoal = z.infer<typeof OfficeGoalSchema>;

export const OfficeFreshnessInfoSchema = z.object({
  locationId: z.uuid(),
  /**
   * Oldest last success of the KPI sources (procedures, appointments): the
   * age of the numbers (V08 one freshness rule). `null` for an office with
   * no PMS link (Velora is the record).
   */
  lastSyncedAt: z.string().nullable(),
  overlay: z.boolean(),
  /** "Synced 3m ago" or the stale source ("procedures 1h 50m behind"). */
  label: z.string().optional(),
  /** A KPI source is more than twice its target behind. */
  stale: z.boolean().optional(),
});

export const BoardSchema = z.object({
  period: ResolvedPeriodSchema,
  comparePeriod: ResolvedPeriodSchema,
  preset: MetricPresetSchema,
  scope: ScopeInfoSchema,
  tiles: z.array(BoardTileSchema),
  pace: PaceSchema,
  forecast: ForecastSchema.nullable(),
  funnel: FunnelSchema,
  /**
   * Case acceptance by case (effective acceptance: booked, completed or
   * marked accepted; Dentrix legacy cases left out) for cases presented in
   * the period. Null when it could not be read.
   */
  caseAcceptance: AcceptanceFiguresSchema.extend({
    legacyExcluded: z.number().int(),
    rule: z.string(),
  })
    .nullable()
    .optional(),
  hygiene: HygieneSummarySchema,
  newPatients: NewPatientsSummarySchema,
  lag: CollectionsLagSchema.nullable(),
  goals: z.array(OfficeGoalSchema),
  freshness: z.array(OfficeFreshnessInfoSchema),
  providers: z.array(ProviderRefSchema),
  /** Plain-language observations computed in code from the numbers above. */
  insights: z.array(z.string()),
  /** Why the collections lag is missing (ledger not synced), when it is. */
  lagUnavailable: MetricGapSchema.nullable(),
  /** Fee outliers waiting in Data hygiene (V08 outlier guard). */
  dataHygiene: z.object({
    outliers: z.number().int(),
    unacknowledged: z.number().int(),
    /** Fees of the unacknowledged outliers; `null` when money is hidden. */
    cents: z.number().int().nullable(),
  }),
  generatedAt: z.string(),
  /** Stored result: when computed and whether a refresh is queued. */
  snapshot: SnapshotInfoSchema.optional(),
});
export type Board = z.infer<typeof BoardSchema>;

/* ------------------------------------------------------------------------ */
/* Drill-down rows                                                           */
/* ------------------------------------------------------------------------ */

export const MetricRowsQuerySchema = z.object({
  period: BoardPeriodSchema.default('mtd'),
  /** Omitted: the office's (else the practice's) default preset. */
  preset: MetricPresetSchema.optional(),
  date: IsoDateSchema.optional(),
  /** Component to list; defaults to the value (or numerator) component. */
  component: z.string().max(64).optional(),
  /** Limit to one office (must be in scope). */
  locationId: z.uuid().optional(),
  /** Limit to one provider (`unassigned` for rows without one). */
  providerId: z.string().max(64).optional(),
  /** Custom range (both or neither): the pivot's from/to. Overrides `period`. */
  from: IsoDateSchema.optional(),
  to: IsoDateSchema.optional(),
  /** Pivot drill: the dimension and the group key whose rows to list (with `group`). */
  dimension: PivotDimensionSchema.optional(),
  group: z.string().max(200).optional(),
});
export type MetricRowsQuery = z.input<typeof MetricRowsQuerySchema>;

export const DRILL_ROW_KINDS = [
  'procedure',
  'ledger',
  'appointment',
  'patient',
  'visit',
  'availability',
  'account',
  /** A row of an extension fact table (calls, benefits, openings ...). */
  'fact',
] as const;

export const DrillRowSchema = z.object({
  ref: z.string(),
  kind: z.enum(DRILL_ROW_KINDS),
  date: IsoDateSchema.nullable(),
  patientId: z.uuid().nullable(),
  patientName: z.string().nullable(),
  locationId: z.uuid(),
  providerId: z.string().nullable(),
  /** Code, entry type or a short description of the source row. */
  detail: z.string(),
  /** Amount in the component's unit; `null` when money is hidden. */
  amount: z.number().nullable(),
});
export type DrillRow = z.infer<typeof DrillRowSchema>;

export const MetricRowsSchema = z.object({
  metricId: z.string(),
  name: z.string(),
  unit: MetricUnitSchema,
  period: ResolvedPeriodSchema,
  component: z.string(),
  componentDescription: z.string(),
  componentUnit: z.enum(['cents', 'count', 'minutes']),
  /** Every component of the metric, for the picker. */
  components: z.array(DictionaryComponentSchema),
  /** The component's total for the requested slice (equals the tile part). */
  total: z.number().nullable(),
  /** Sum of `amount` over all rows; equals `total` (a test pins it). */
  rowsTotal: z.number().nullable(),
  /** Value of the metric for the requested slice. */
  value: z.number().nullable(),
  rows: z.array(DrillRowSchema),
  /** Rows behind the total; `rows` is cut at `ROW_LIMIT` (totals still cover all of them). */
  rowCount: z.number().int(),
  truncated: z.boolean(),
  moneyHidden: z.boolean(),
  /** Offices left out because their PMS link does not sync a feed the metric needs (V08). */
  unavailable: z.array(OfficeGapSchema).optional(),
});
export const ROW_LIMIT = 20_000;
export type MetricRows = z.infer<typeof MetricRowsSchema>;

/* ------------------------------------------------------------------------ */
/* Huddle                                                                    */
/* ------------------------------------------------------------------------ */

/** `last_week`: Monday to Sunday of the previous week (the Monday huddle tab). */
export const HUDDLE_DAYS = ['yesterday', 'today', 'tomorrow', 'last_week'] as const;
export const HuddleDaySchema = z.enum(HUDDLE_DAYS);
export type HuddleDay = z.infer<typeof HuddleDaySchema>;

/**
 * How far a dated huddle may reach around practice-local today (Home's day
 * picker, `day=YYYY-MM-DD`): the past 30 days and the next 14.
 */
export const HUDDLE_DATE_PAST_DAYS = 30;
export const HUDDLE_DATE_FUTURE_DAYS = 14;

export const HuddleQuerySchema = z.object({
  /**
   * A tab (`today`, `tomorrow` = the next working day, ...) or one calendar
   * day, `YYYY-MM-DD`, inside HUDDLE_DATE_PAST_DAYS / HUDDLE_DATE_FUTURE_DAYS
   * of today (Home's day picker). A dated day is the same date in every office.
   */
  day: z.union([HuddleDaySchema, IsoDateSchema]).default('today'),
  /** Practice-local "today" override (tests, history). */
  date: IsoDateSchema.optional(),
});
export type HuddleQuery = z.input<typeof HuddleQuerySchema>;

export const HUDDLE_FLAGS = [
  'new_patient',
  'unconfirmed',
  'medical_alert',
  'balance_due',
  'unscheduled_treatment',
  'unpriced',
  /** No bitewings or full-mouth X-rays in the 12 months before the visit (WP-14). */
  'xrays_due',
  /** A household member with no future appointment (DI "family" chip, WP-14). */
  'family_due',
  /** The patient's birthday is the visit day (the huddle sheet's Birthdays). */
  'birthday',
] as const;
export const HuddleFlagSchema = z.enum(HUDDLE_FLAGS);
export type HuddleFlag = z.infer<typeof HuddleFlagSchema>;

export const HuddleAppointmentSchema = z.object({
  id: z.uuid(),
  locationId: z.uuid(),
  startsAt: z.string(),
  lengthMin: z.number().int(),
  patientId: z.uuid(),
  patientName: z.string(),
  providerId: z.uuid().nullable(),
  operatory: z.string().nullable(),
  /** OD ApptStatus: 1 scheduled, 2 complete, 5 broken. */
  aptStatus: z.number().int(),
  flowStatus: z.string(),
  confirmation: z.string(),
  codes: z.array(z.string()),
  productionCents: z.number().int().nullable(),
  /**
   * The production is estimated from the visit type's procedures and the
   * office fee schedule (no procedures attached, no PMS amount). Owner role
   * test: 64 of 79 visits were "Unpriced" and scheduled production read 4x low.
   */
  productionEstimated: z.boolean().default(false),
  balanceCents: z.number().int().nullable(),
  unscheduledTxCents: z.number().int().nullable(),
  alerts: z.array(z.string()),
  flags: z.array(HuddleFlagSchema),
});
export type HuddleAppointment = z.infer<typeof HuddleAppointmentSchema>;

export const HuddleProviderRowSchema = z.object({
  providerId: z.string(),
  locationId: z.uuid(),
  patients: z.number().int(),
  scheduledCents: z.number().int().nullable(),
  bookedMinutes: z.number().int(),
  availableMinutes: z.number().int(),
  /** Available minutes not covered by an appointment. */
  openMinutes: z.number().int(),
  /** The office's daily goal shared by scheduled minutes; null: no goal, money hidden or not scheduled. */
  dailyGoalCents: z.number().int().nullable().optional(),
});

export const HuddleTilesSchema = z.object({
  appointments: z.number().int(),
  scheduledCents: z.number().int().nullable(),
  /** Daily production goal: the monthly goal weighted by provider schedule, else clinical hours by weekday, else working days. */
  dailyGoalCents: z.number().int().nullable(),
  unconfirmed: z.number().int(),
  newPatients: z.number().int(),
  medicalAlerts: z.number().int(),
  collectAtDeskCents: z.number().int().nullable(),
  unscheduledInChairPatients: z.number().int(),
  unscheduledInChairCents: z.number().int().nullable(),
  bookedMinutes: z.number().int(),
  /**
   * Booked minutes of providers who have scheduled hours that day: the
   * numerator of "provider hours booked" (`bookedShare(scheduledBookedMinutes,
   * availableMinutes)`). `bookedMinutes` also holds visits of offices and
   * providers without a provider schedule, which have no denominator.
   */
  scheduledBookedMinutes: z.number().int().optional(),
  availableMinutes: z.number().int(),
  openMinutes: z.number().int(),
  unpriced: z.number().int(),
  /** Visits priced by their visit type's fees (inside scheduledCents), and those dollars. */
  estimated: z.number().int().default(0),
  estimatedCents: z.number().int().nullable().default(0),
  completed: z.number().int(),
  missed: z.number().int(),
  /**
   * Tiles the office's PMS link cannot supply (V08), keyed by tile name: the
   * tile shows "Not synced from <PMS>" instead of its zero. On the total row
   * a key is present only when no office in view can supply the tile.
   */
  unavailable: z.record(z.string(), MetricGapSchema),
  /** Total row only: per tile, how many offices were left out because they cannot supply it. */
  unavailableOffices: z.record(z.string(), z.number().int()),
  /**
   * Tiles counted from incomplete data (V08): Velora-recorded alerts or
   * new-patient visits while the PMS's own are not synced. Shown with a
   * "Partial" note, never as the complete number.
   */
  partial: z.record(z.string(), MetricGapSchema).optional(),
});
export type HuddleTiles = z.infer<typeof HuddleTilesSchema>;

export const DataHygieneItemSchema = z.object({
  kind: z.enum(['zero_fee', 'no_provider', 'fee_outlier']),
  procedureId: z.uuid(),
  patientId: z.uuid(),
  patientName: z.string(),
  locationId: z.uuid(),
  code: z.string(),
  feeCents: z.number().int().nullable(),
  expectedFeeCents: z.number().int().nullable(),
});

export const HuddleYesterdaySchema = z.object({
  productionCents: z.number().int().nullable(),
  collectionsCents: z.number().int().nullable(),
  visits: z.number().int(),
  missed: z.number().int(),
  dataHygiene: z.array(DataHygieneItemSchema),
  /** Distinct patients with a completed visit. */
  patientsSeen: z.number().int().optional(),
  newPatients: z.number().int().optional(),
  /** Production goal for the day(s) (weighted daily goals); null without a goal or money hidden. */
  goalCents: z.number().int().nullable().optional(),
  /** Patients seen with no later appointment scheduled now. */
  notRescheduled: z.number().int().optional(),
});

export const HUDDLE_OPPORTUNITIES = [
  'unscheduled_family',
  'unscheduled_treatment',
  'ar_balance',
  'unscheduled_hygiene',
] as const;
export const HuddleOpportunityKindSchema = z.enum(HUDDLE_OPPORTUNITIES);
export type HuddleOpportunityKind = z.infer<typeof HuddleOpportunityKindSchema>;

export const HuddleOpportunityItemSchema = z.object({
  patientId: z.uuid(),
  patientName: z.string(),
  locationId: z.uuid(),
  /** The opportunity when the day began (unscheduled treatment, balance due, estimated hygiene fee). */
  cents: z.number().int().nullable(),
  /** What is still open now. */
  remainingCents: z.number().int().nullable(),
  /** Acted on since (booked, paid); always false for today and later. */
  completed: z.boolean(),
});

export const HuddleOpportunityListSchema = z.object({
  kind: HuddleOpportunityKindSchema,
  label: z.string(),
  items: z.array(HuddleOpportunityItemSchema),
  completed: z.number().int(),
  notCompleted: z.number().int(),
  /** Remaining amount over the not-completed items (the "$X unscheduled" line). */
  unscheduledCents: z.number().int().nullable(),
  /** Amount is an estimate (hygiene: the patient's last hygiene fees). */
  estimated: z.boolean(),
  /** The office's PMS link cannot supply this list (households, ledger). */
  unavailable: MetricGapSchema.nullable(),
});
export type HuddleOpportunityList = z.infer<typeof HuddleOpportunityListSchema>;

export const HuddleNotRescheduledSchema = z.object({
  count: z.number().int(),
  items: z.array(
    z.object({
      patientId: z.uuid(),
      patientName: z.string(),
      locationId: z.uuid(),
      unscheduledTxCents: z.number().int().nullable(),
    }),
  ),
});
export type HuddleNotRescheduled = z.infer<typeof HuddleNotRescheduledSchema>;

export const HuddleOfficeSchema = z.object({
  locationId: z.uuid(),
  /** The office's own date for this tab (next working day for "tomorrow", AH-10). */
  date: IsoDateSchema,
  /** Last day covered (the Sunday for last_week; else the same as date). */
  endDate: IsoDateSchema.optional(),
  tiles: HuddleTilesSchema,
  providers: z.array(HuddleProviderRowSchema),
  appointments: z.array(HuddleAppointmentSchema),
  yesterday: HuddleYesterdaySchema.nullable(),
  forecast: ForecastOfficeSchema,
  opportunities: z.array(HuddleOpportunityListSchema).optional(),
  notRescheduled: HuddleNotRescheduledSchema.nullable().optional(),
});
export type HuddleOffice = z.infer<typeof HuddleOfficeSchema>;

export const HuddleSchema = z.object({
  /** The tab; a dated request (`day=YYYY-MM-DD`) answers as yesterday (past), today or tomorrow (future). */
  day: HuddleDaySchema,
  /** The calendar day asked for with `day=YYYY-MM-DD`; absent for a tab. */
  requestedDate: IsoDateSchema.optional(),
  /** Practice-local today. */
  today: IsoDateSchema,
  scope: ScopeInfoSchema,
  offices: z.array(HuddleOfficeSchema),
  total: HuddleTilesSchema,
  totalYesterday: HuddleYesterdaySchema.nullable(),
  totalForecast: ForecastSchema.shape.total,
  totalOpportunities: z.array(HuddleOpportunityListSchema).optional(),
  totalNotRescheduled: HuddleNotRescheduledSchema.nullable().optional(),
  providers: z.array(ProviderRefSchema),
  freshness: z.array(OfficeFreshnessInfoSchema),
  /** Sentences computed in code from the numbers (no AI). */
  narrative: z.array(z.string()),
  generatedAt: z.string(),
  /** Stored result: when computed and whether a refresh is queued. */
  snapshot: SnapshotInfoSchema.optional(),
});
export type Huddle = z.infer<typeof HuddleSchema>;

/**
 * `GET /huddle/summary`: today's priorities in a few plain sentences for the
 * top of the huddle. Worded by the practice's AI lane (the BAA vendor where
 * the practice is cleared, else the on-premises model) from the huddle's own
 * numbers given as placeholders: the model never writes a number, Velora
 * fills them in and checks them. Without a model, or when a check fails, the
 * code-built narrative is used (`engine: 'template'`). No patient names.
 */
export const HuddleSummarySchema = z.object({
  text: z.string(),
  engine: z.enum(['ai', 'template']),
  /** Why the template was used, when it was. */
  note: z.string().nullable(),
  generatedAt: z.string(),
});
export type HuddleSummary = z.infer<typeof HuddleSummarySchema>;

/* ------------------------------------------------------------------------ */
/* Provider scorecards                                                       */
/* ------------------------------------------------------------------------ */

export const SCORECARD_METRICS = [
  'production_gross',
  'production_net',
  'collections',
  'collections_insurance',
  'collections_patient',
  'same_day_treatment',
  'visits',
  'production_per_visit',
  'production_per_day',
  'production_per_available_hour',
  'production_per_booked_hour',
  'patients_seen',
  'new_patients',
  'patient_diagnostic_rate',
  'case_acceptance_dollars',
  'hygiene_reappointment_rate',
] as const;
export type ScorecardMetric = (typeof SCORECARD_METRICS)[number];

export const ScorecardQuerySchema = z.object({
  period: BoardPeriodSchema.default('mtd'),
  /**
   * A custom date range, inclusive (both or neither; owner debrief
   * 2026-10-28: People had no date-range filter). Overrides `period`.
   */
  from: IsoDateSchema.optional(),
  to: IsoDateSchema.optional(),
  /** Omitted: the office's (else the practice's) default preset. */
  preset: MetricPresetSchema.optional(),
  date: IsoDateSchema.optional(),
});

export const ScorecardRowSchema = z.object({
  providerId: z.string(),
  metrics: z.record(z.string(), MetricCellSchema),
  /** Same metrics per office where the provider produced or saw patients. */
  offices: z.array(
    z.object({ locationId: z.uuid(), metrics: z.record(z.string(), MetricCellSchema) }),
  ),
});
export type ScorecardRow = z.infer<typeof ScorecardRowSchema>;

export const SCORECARD_ROLES = ['dentist', 'hygienist', 'other'] as const;
export const ScorecardRoleSchema = z.enum(SCORECARD_ROLES);
export type ScorecardRole = z.infer<typeof ScorecardRoleSchema>;

export const ScorecardsSchema = z.object({
  period: ResolvedPeriodSchema,
  preset: MetricPresetSchema,
  scope: ScopeInfoSchema,
  metrics: z.array(
    z.object({ id: z.string(), name: z.string(), unit: MetricUnitSchema, money: z.boolean() }),
  ),
  rows: z.array(ScorecardRowSchema),
  providers: z.array(ProviderRefSchema),
  /**
   * Per role (dentist / hygienist / other: assistants and unassigned rows),
   * every metric over that role's rows (a ratio is the role's sums, never an
   * average of providers; a patient seen by two dentists counts once).
   */
  roles: z
    .array(z.object({ role: ScorecardRoleSchema, metrics: z.record(z.string(), MetricCellSchema) }))
    .optional(),
  /**
   * Offices whose PMS link cannot supply a metric: named, never silently
   * dropped (owner role test: 3 of 5 offices vanished).
   */
  officeGaps: z.array(OfficeGapSchema.extend({ metricId: z.string() })).optional(),
});
export type Scorecards = z.infer<typeof ScorecardsSchema>;

/* ------------------------------------------------------------------------ */
/* Patient Finder                                                            */
/* ------------------------------------------------------------------------ */

export const FINDER_SEGMENTS = [
  'unscheduled_treatment',
  'overdue_recall',
  'no_future_appointment',
  'balance_due',
  'new_no_return',
] as const;
export const FinderSegmentSchema = z.enum(FINDER_SEGMENTS);
export type FinderSegment = z.infer<typeof FinderSegmentSchema>;

export const FINDER_SEGMENT_INFO: Record<
  FinderSegment,
  { label: string; description: string; valueLabel: string }
> = {
  unscheduled_treatment: {
    label: 'Unscheduled treatment',
    description:
      'Treatment-planned procedures that are not on a scheduled appointment. Value is the planned fee.',
    valueLabel: 'Planned fee',
  },
  overdue_recall: {
    label: 'Overdue recall',
    description:
      'Active patients whose hygiene or perio recall is past due with no hygiene visit booked.',
    valueLabel: 'Days overdue',
  },
  no_future_appointment: {
    label: 'No future appointment',
    description: 'Active patients seen in the last 18 months with nothing on the schedule.',
    valueLabel: 'Days since visit',
  },
  balance_due: {
    label: 'Balance over 30 days',
    description: 'Guarantors with a balance aged over 30 days. Value is the past-due amount.',
    valueLabel: 'Past due',
  },
  new_no_return: {
    label: 'New patients not rebooked',
    description: 'First visit in the last 90 days and no future appointment.',
    valueLabel: 'Days since first visit',
  },
};

export const FinderQuerySchema = z.object({
  segment: FinderSegmentSchema.default('unscheduled_treatment'),
  /** Minimum value (cents for money segments, days otherwise). */
  min: z.coerce.number().int().min(0).optional(),
  /** Last visit date range, inclusive (owner debrief 2026-10-28: People date ranges). */
  lastVisitFrom: IsoDateSchema.optional(),
  lastVisitTo: IsoDateSchema.optional(),
  date: IsoDateSchema.optional(),
});
export type FinderQuery = z.input<typeof FinderQuerySchema>;

export const FinderRowSchema = z.object({
  patientId: z.uuid(),
  patientName: z.string(),
  locationId: z.uuid(),
  lastVisit: IsoDateSchema.nullable(),
  nextAppointment: IsoDateSchema.nullable(),
  /** Cents for money segments (`null` when hidden), days otherwise. */
  value: z.number().nullable(),
  detail: z.string(),
  hasPhone: z.boolean(),
});
export type FinderRow = z.infer<typeof FinderRowSchema>;

export const FinderResultSchema = z.object({
  segment: FinderSegmentSchema,
  valueUnit: z.enum(['cents', 'days']),
  asOf: IsoDateSchema,
  scope: ScopeInfoSchema,
  rows: z.array(FinderRowSchema),
  totalValue: z.number().nullable(),
  /** Every matching row (HA-10); `rows` stops at the row limit. */
  total: z.number().int(),
  /** True when `rows` holds fewer than `total` (shown as "2,000 of N"; the export has all). */
  truncated: z.boolean(),
  /** Offices this segment cannot be built for (a feed is not synced). */
  unavailable: z.array(OfficeGapSchema),
  /** Offices where the segment is built from incomplete data (labelled partial). */
  partial: z.array(OfficeGapSchema),
});
export type FinderResult = z.infer<typeof FinderResultSchema>;

export const FinderTasksBodySchema = z.object({
  segment: FinderSegmentSchema,
  patientIds: z.array(z.uuid()).min(1).max(500),
  title: z.string().trim().min(1).max(120).optional(),
  dueDate: IsoDateSchema.optional(),
});
export type FinderTasksBody = z.infer<typeof FinderTasksBodySchema>;

export const FinderTasksResultSchema = z.object({
  created: z.number().int(),
  skipped: z.number().int(),
});

/* ------------------------------------------------------------------------ */
/* Pivot scorecards (Dental Intelligence Scorecards parity, gap #5)          */
/* ------------------------------------------------------------------------ */

export const PIVOT_DIMENSION_INFO: Record<PivotDimension, { label: string; description: string }> =
  {
    office: { label: 'Office', description: 'Each office in the current view.' },
    provider: {
      label: 'Provider',
      description: 'The rendering provider of each row (grouped by dentist / hygienist).',
    },
    role: { label: 'Provider role', description: 'Dentists, hygienists and everyone else.' },
    procedure_category: {
      label: 'Procedure category',
      description: 'ADA CDT category of service (Diagnostic, Preventive, Restorative ...).',
    },
    procedure_code: { label: 'Procedure code', description: 'Each procedure code.' },
    referral_source: {
      label: 'Referral source',
      description: "The patient's referral source as the PMS records it.",
    },
    carrier: {
      label: 'Insurance carrier',
      description: "The patient's primary insurance carrier today (no carrier = self-pay).",
    },
    team_member: {
      label: 'Team member',
      description:
        "The team member assigned the patient's most recent follow-up task by the period end. The PMS does not say who booked or posted, so this is follow-up attribution.",
    },
    appt_weekday: {
      label: 'Appointment weekday',
      description: 'The weekday of the appointment behind each row (office time).',
    },
    appt_hour: {
      label: 'Appointment hour',
      description: 'The hour the appointment behind each row started (office time).',
    },
    staff_member: {
      label: 'Staff member',
      description:
        'The desk (phone extension) or Velora user who acted: made the first contact, answered the call, replied to the text.',
    },
    lead_source: {
      label: 'Source',
      description:
        'Where the lead or new patient came from (missed call, web chat, Google Ads ...).',
    },
    demand_reason: {
      label: 'Reason turned away',
      description:
        'Why a call could not be served: Medicaid or plan not accepted, service not offered, hours.',
    },
    price_band: {
      label: 'Price band',
      description: 'The presented fees of the treatment-plan case.',
    },
    payer_type: {
      label: 'Insurance or cash',
      description: 'Whether the patient had an insurance plan in force on the case date.',
    },
    followup_step: {
      label: 'Follow-up step',
      description:
        'The last Case Closed follow-up before the case was accepted (or the last one made).',
    },
    claim_carrier: {
      label: 'Claim carrier',
      description: 'The insurance carrier on the claim, as the PMS names it.',
    },
    denial_reason: {
      label: 'Denial reason',
      description: 'The first adjustment reason code (CARC) in the EOB remarks the PMS sent.',
    },
    payment_type: {
      label: 'Payment type',
      description:
        'The PMS payment type of each payment (cash, check, card ...), under patient or insurance payments.',
    },
    booking_lead: {
      label: 'Booking lead time',
      description:
        'How far ahead the appointment behind each row was booked (PMS booking date; unknown when the PMS does not send it).',
    },
  };

/** At most this many metrics per pivot (one compute each). */
export const PIVOT_MAX_METRICS = 8;

export const PivotQuerySchema = z.object({
  dimension: PivotDimensionSchema.default('provider'),
  /** Comma-separated metric ids from the dictionary (1 to PIVOT_MAX_METRICS). */
  metrics: z
    .string()
    .regex(/^[a-z][a-z0-9_]*(,[a-z][a-z0-9_]*)*$/)
    .default('production_gross,collections,visits'),
  period: BoardPeriodSchema.default('mtd'),
  /** A custom range, inclusive (both or neither). Overrides `period`. */
  from: IsoDateSchema.optional(),
  to: IsoDateSchema.optional(),
  /** Comparison column: the prior period of the same length, or the same dates last year. */
  compare: z.enum(['none', 'prior', 'year']).default('none'),
  /** Trend columns (WP-16): the trailing weeks, months or quarters ending on the period end. */
  trend: z.enum(['none', 'week', 'month', 'quarter']).default('none'),
  trendCount: z.coerce.number().int().min(2).max(12).default(6),
  /** Goal overlay (WP-16): each cell against the office's (or provider's) own goal. */
  goals: z.enum(['0', '1']).default('0'),
  /** Practice benchmark (WP-16): each cell against the spread of the practice's own groups. */
  bench: z.enum(['0', '1']).default('0'),
  preset: MetricPresetSchema.optional(),
  date: IsoDateSchema.optional(),
});
export type PivotQuery = z.input<typeof PivotQuerySchema>;

export const PivotCellSchema = MetricCellSchema.extend({
  /** The comparison period's value (same rules); absent without `compare`. */
  compare: z.number().nullable().optional(),
  /** Values over the trend buckets (`trendBuckets`), oldest first. */
  trend: z.array(z.number().nullable()).optional(),
  /** The goal for this cell's group and period, and how the value stands against it. */
  goal: z
    .object({
      goal: z.number(),
      attainment: z.number().nullable(),
      gap: z.number(),
      met: z.boolean(),
    })
    .nullable()
    .optional(),
  /** Where the value sits among the practice's own groups. */
  band: z.enum(['best', 'above_median', 'below_median', 'weakest', 'only']).nullable().optional(),
});
export type PivotCell = z.infer<typeof PivotCellSchema>;

export const PivotGroupSchema = z.object({
  key: z.string(),
  label: z.string(),
  /** Parent group: CDT category of a code, role of a provider. */
  parent: z.string().nullable(),
  parentLabel: z.string().nullable(),
  values: z.record(z.string(), PivotCellSchema),
});
export type PivotGroup = z.infer<typeof PivotGroupSchema>;

export const PivotMetricInfoSchema = z.object({
  id: z.string(),
  name: z.string(),
  unit: MetricUnitSchema,
  money: z.boolean(),
  better: z.enum(['higher', 'lower', 'neutral']),
  /** Set when the metric cannot be split by this dimension (the column is empty). */
  refused: z.string().nullable(),
  /** False when a patient sits in several groups (union total): the groups do not add up. */
  groupsSumToTotal: z.boolean(),
});

export const PivotResultSchema = z.object({
  dimension: PivotDimensionSchema,
  period: ResolvedPeriodSchema,
  comparePeriod: ResolvedPeriodSchema.nullable(),
  preset: MetricPresetSchema,
  scope: ScopeInfoSchema,
  metrics: z.array(PivotMetricInfoSchema),
  groups: z.array(PivotGroupSchema),
  total: z.record(z.string(), PivotCellSchema),
  /** Offices left out of a metric because their PMS link cannot supply it. */
  officeGaps: z.array(OfficeGapSchema.extend({ metricId: z.string() })),
  notes: z.array(z.string()),
  /** Trend column buckets (empty without `trend`). */
  trendBuckets: z
    .array(z.object({ start: z.string(), end: z.string(), label: z.string() }))
    .default([]),
  /** Practice band per metric (best / median / weakest of its own groups); with `bench`. */
  bands: z
    .record(
      z.string(),
      z.object({
        offices: z.number().int(),
        best: z.number(),
        median: z.number(),
        weakest: z.number(),
        bestKey: z.string(),
      }),
    )
    .default({}),
  generatedAt: z.string(),
});
export type PivotResult = z.infer<typeof PivotResultSchema>;

/** Ready-made pivots (DI Scorecard templates). */
export const PIVOT_TEMPLATES: ReadonlyArray<{
  id: string;
  label: string;
  dimension: PivotDimension;
  metrics: readonly string[];
}> = [
  {
    id: 'doctor',
    label: 'Doctor',
    dimension: 'provider',
    metrics: [
      'production_gross',
      'production_per_day',
      'patient_diagnostic_rate',
      'case_acceptance_restorative',
      'same_day_treatment',
      'collections',
    ],
  },
  {
    id: 'hygiene',
    label: 'Hygiene',
    dimension: 'provider',
    metrics: [
      'production_gross',
      'visits',
      'production_per_visit',
      'hygiene_reappointment_rate',
      'case_acceptance_hygiene',
    ],
  },
  {
    id: 'procedure_mix',
    label: 'Procedure mix',
    dimension: 'procedure_category',
    metrics: [
      'production_gross',
      'procedures_completed',
      'case_acceptance_dollars',
      'unscheduled_treatment',
    ],
  },
  {
    id: 'marketing',
    label: 'Marketing (referrals)',
    dimension: 'referral_source',
    metrics: ['new_patients', 'production_gross', 'collections', 'case_acceptance_dollars'],
  },
  {
    id: 'payer_mix',
    label: 'Payer mix',
    dimension: 'carrier',
    metrics: ['production_gross', 'collections', 'writeoffs', 'patients_seen', 'ar_total'],
  },
  {
    id: 'operations',
    label: 'Operations',
    dimension: 'office',
    metrics: [
      'production_gross',
      'production_net',
      'collections',
      'collection_rate',
      'new_patients',
      'schedule_utilization',
    ],
  },
  {
    id: 'front_office',
    label: 'Front office',
    dimension: 'office',
    metrics: [
      'preappointment_rate',
      'no_show_rate',
      'hygiene_reappointment_rate',
      'unscheduled_treatment',
    ],
  },
  {
    id: 'team',
    label: 'Team follow-up',
    dimension: 'team_member',
    metrics: ['production_gross', 'collections', 'case_acceptance_dollars', 'patients_seen'],
  },
];

/* ------------------------------------------------------------------------ */
/* Patient Finder search (Dental Intelligence depth, gap #14)                */
/* ------------------------------------------------------------------------ */

const csvList = (max: number) => z.array(z.string().trim().min(1).max(120)).max(max).default([]);

/** CDT categories of service (keys match @velora/domain/metrics cdtCategory). */
export const CDT_CATEGORY_OPTIONS: ReadonlyArray<{ value: string; label: string }> = [
  { value: 'diagnostic', label: 'Diagnostic' },
  { value: 'preventive', label: 'Preventive' },
  { value: 'restorative', label: 'Restorative' },
  { value: 'endodontics', label: 'Endodontics' },
  { value: 'periodontics', label: 'Periodontics' },
  { value: 'prosth_removable', label: 'Removable prosthodontics' },
  { value: 'maxillofacial', label: 'Maxillofacial prosthetics' },
  { value: 'implants', label: 'Implant services' },
  { value: 'prosth_fixed', label: 'Fixed prosthodontics' },
  { value: 'oral_surgery', label: 'Oral surgery' },
  { value: 'orthodontics', label: 'Orthodontics' },
  { value: 'adjunctive', label: 'Adjunctive services' },
  { value: 'other', label: 'Other codes' },
];

/** Every filter is optional; set filters combine with AND. */
export const FinderFiltersSchema = z.object({
  /** Patient status (default active). */
  status: z.enum(['active', 'inactive', 'all']).default('active'),
  /** Home office (in scope). */
  officeIds: z.array(z.uuid()).max(50).default([]),
  /* Visits */
  lastVisitFrom: IsoDateSchema.optional(),
  lastVisitTo: IsoDateSchema.optional(),
  /** The provider of the last completed visit. */
  lastVisitProviderIds: z.array(z.string().max(64)).max(50).default([]),
  /** Next appointment: any booked, none booked. */
  nextVisit: z.enum(['any', 'booked', 'none']).default('any'),
  nextVisitFrom: IsoDateSchema.optional(),
  nextVisitTo: IsoDateSchema.optional(),
  nextVisitProviderIds: z.array(z.string().max(64)).max(50).default([]),
  /** The next appointment is not confirmed. */
  nextVisitUnconfirmed: z.boolean().optional(),
  /** Hygiene booked: a future hygiene appointment exists or not. */
  hygieneScheduled: z.enum(['any', 'yes', 'no']).default('any'),
  /* Recall */
  recall: z.enum(['any', 'overdue', 'due_soon', 'none']).default('any'),
  /** Due-soon window, days. */
  recallDays: z.number().int().min(1).max(365).default(30),
  /** Hygiene type: the patient's hygiene recall (prophy / perio maintenance). */
  hygieneType: z.enum(['any', 'prophy', 'perio']).default('any'),
  /* Money (money.view only; ignored without it) */
  balanceMinCents: z.number().int().min(0).optional(),
  balanceMaxCents: z.number().int().min(0).optional(),
  /** Past-due bucket: family balance aged over 30 / 60 / 90 days. */
  pastDue: z.enum(['any', 'over_30', 'over_60', 'over_90']).default('any'),
  /* Treatment */
  unscheduledMinCents: z.number().int().min(0).optional(),
  unscheduledMaxCents: z.number().int().min(0).optional(),
  /** Unscheduled treatment includes one of these codes (prefix, e.g. D27). */
  unscheduledCodes: csvList(30),
  /** Unscheduled treatment includes a code in one of these CDT categories. */
  unscheduledCategories: csvList(13),
  /* Patient */
  ageMin: z.number().int().min(0).max(130).optional(),
  ageMax: z.number().int().min(0).max(130).optional(),
  birthdayThisMonth: z.boolean().optional(),
  gender: z.enum(['any', 'female', 'male', 'other']).default('any'),
  /** Primary provider. */
  providerIds: z.array(z.string().max(64)).max(50).default([]),
  /** Carrier names (primary plan); `insured` / `uninsured` limit to either. */
  insurance: z.enum(['any', 'insured', 'uninsured']).default('any'),
  carriers: csvList(50),
  referralSources: csvList(50),
  grades: z
    .array(z.enum(['A', 'B', 'C', 'D', 'F']))
    .max(5)
    .default([]),
  /** No-shows and broken appointments in the last 12 months, at least. */
  noShowsMin: z.number().int().min(1).max(50).optional(),
  hasMobile: z.enum(['any', 'yes', 'no']).default('any'),
  hasEmail: z.enum(['any', 'yes', 'no']).default('any'),
  /** Text / email opt-out: any opted out, or reachable (not opted out). */
  optedOut: z.enum(['any', 'sms', 'email', 'none']).default('any'),
  familyRole: z.enum(['any', 'guarantor', 'dependent']).default('any'),
  /** New patient: first visit in this range. */
  newPatientFrom: IsoDateSchema.optional(),
  newPatientTo: IsoDateSchema.optional(),
});
export type FinderFilters = z.infer<typeof FinderFiltersSchema>;
export type FinderFiltersInput = z.input<typeof FinderFiltersSchema>;

export const FINDER_SORTS = [
  'last_visit',
  'next_visit',
  'balance',
  'unscheduled',
  'name',
  'age',
] as const;

export const FinderSearchBodySchema = z.object({
  filters: FinderFiltersSchema.prefault({}),
  sort: z.enum(FINDER_SORTS).default('unscheduled'),
  dir: z.enum(['asc', 'desc']).default('desc'),
  date: IsoDateSchema.optional(),
});
export type FinderSearchBody = z.input<typeof FinderSearchBodySchema>;

export const FinderSearchRowSchema = z.object({
  patientId: z.uuid(),
  patientName: z.string(),
  locationId: z.uuid().nullable(),
  age: z.number().int().nullable(),
  lastVisit: IsoDateSchema.nullable(),
  nextVisit: IsoDateSchema.nullable(),
  recallDue: IsoDateSchema.nullable(),
  /** Family balance and its past-due part; `null` when money is hidden or the ledger is not synced. */
  balanceCents: z.number().int().nullable(),
  pastDueCents: z.number().int().nullable(),
  unscheduledCents: z.number().int().nullable(),
  unscheduledCodes: z.array(z.string()),
  carrier: z.string().nullable(),
  referralSource: z.string().nullable(),
  grade: z.string().nullable(),
  noShows: z.number().int(),
  hasMobile: z.boolean(),
  hasEmail: z.boolean(),
  smsOptedOut: z.boolean(),
  emailOptedOut: z.boolean(),
  guarantor: z.boolean(),
});
export type FinderSearchRow = z.infer<typeof FinderSearchRowSchema>;

export const FinderSearchResultSchema = z.object({
  asOf: IsoDateSchema,
  scope: ScopeInfoSchema,
  rows: z.array(FinderSearchRowSchema),
  total: z.number().int(),
  truncated: z.boolean(),
  /** Sum over every match (not only the rows shown); `null` when money is hidden. */
  unscheduledTotalCents: z.number().int().nullable(),
  balanceTotalCents: z.number().int().nullable(),
  /** Filters that were ignored (money without money.view) or read partial data. */
  notes: z.array(z.string()),
});
export type FinderSearchResult = z.infer<typeof FinderSearchResultSchema>;

/** The whole search as CSV (every match, not only the rows shown). */
export const FinderSearchExportSchema = z.object({
  filename: z.string(),
  csv: z.string(),
  rows: z.number().int(),
});

/** Bulk actions: a follow-up task, a call list, or a manual text list (opt-outs respected). */
export const FinderActionBodySchema = z.object({
  action: z.enum(['tasks', 'call_list', 'text_list']),
  filters: FinderFiltersSchema.prefault({}),
  /** The selected patients (re-checked against the filters and scope); omitted = every match. */
  patientIds: z.array(z.uuid()).min(1).max(5000).optional(),
  title: z.string().trim().min(1).max(120).optional(),
  dueDate: IsoDateSchema.optional(),
  date: IsoDateSchema.optional(),
});
export type FinderActionBody = z.input<typeof FinderActionBodySchema>;

export const FinderActionResultSchema = z.object({
  created: z.number().int(),
  /** Already had an open finder task. */
  skipped: z.number().int(),
  /** Left out by contact rules, per reason (do not contact, opted out, no mobile ...). */
  excluded: z.array(z.object({ reason: z.string(), label: z.string(), count: z.number().int() })),
});
export type FinderActionResult = z.infer<typeof FinderActionResultSchema>;

export const FinderSavedSearchSchema = z.object({
  id: z.uuid(),
  name: z.string(),
  filters: FinderFiltersSchema,
  shared: z.boolean(),
  mine: z.boolean(),
  updatedAt: z.string(),
});
export type FinderSavedSearch = z.infer<typeof FinderSavedSearchSchema>;

export const FinderSavedSearchesSchema = z.object({ searches: z.array(FinderSavedSearchSchema) });

export const FinderSaveBodySchema = z.object({
  name: z.string().trim().min(1).max(60),
  filters: FinderFiltersSchema,
  /** Shared with the practice's other users (read-only for them). */
  shared: z.boolean().default(false),
});

/** The finder's facet values (carriers, referral sources) for the pickers. */
export const FinderFacetsSchema = z.object({
  carriers: z.array(z.string()),
  referralSources: z.array(z.string()),
  providers: z.array(ProviderRefSchema),
});

/* ------------------------------------------------------------------------ */
/* Reports                                                                   */
/* ------------------------------------------------------------------------ */

export const DaySheetQuerySchema = z.object({ date: IsoDateSchema.optional() });

export const MoneyTotalsSchema = z.object({
  grossCents: z.number().int(),
  writeoffsCents: z.number().int(),
  adjustmentsCents: z.number().int(),
  netCents: z.number().int(),
  patientPaymentsCents: z.number().int(),
  insurancePaymentsCents: z.number().int(),
  collectionsCents: z.number().int(),
});
export type MoneyTotals = z.infer<typeof MoneyTotalsSchema>;

export const DaySheetProcedureSchema = z.object({
  id: z.uuid(),
  locationId: z.uuid(),
  patientId: z.uuid(),
  patientName: z.string(),
  code: z.string(),
  description: z.string(),
  tooth: z.string().nullable(),
  surfaces: z.string().nullable(),
  providerId: z.uuid().nullable(),
  feeCents: z.number().int(),
});

export const DaySheetEntrySchema = z.object({
  id: z.uuid(),
  locationId: z.uuid(),
  patientId: z.uuid(),
  patientName: z.string(),
  kind: z.enum([
    'patient_payment',
    'insurance_payment',
    'writeoff',
    'production_adjustment',
    'collection_adjustment',
  ]),
  /** Payment method or adjustment type. */
  detail: z.string(),
  providerId: z.uuid().nullable(),
  /** Positive = received or deducted, as the totals use it. */
  amountCents: z.number().int(),
});

export const DaySheetSchema = z.object({
  date: IsoDateSchema,
  scope: ScopeInfoSchema,
  procedures: z.array(DaySheetProcedureSchema),
  entries: z.array(DaySheetEntrySchema),
  offices: z.array(MoneyTotalsSchema.extend({ locationId: z.uuid() })),
  providers: z.array(MoneyTotalsSchema.extend({ providerId: z.string() })),
  total: MoneyTotalsSchema,
  providerRefs: z.array(ProviderRefSchema),
  /** Offices whose payments and adjustments are not synced: their money lines are unknown, not $0. */
  ledgerUnavailable: z.array(OfficeGapSchema),
  /**
   * Services posted on the day and deleted in the PMS, per office (Eaglesoft's
   * day sheet "Deleted Services" line: Services - Deleted = Production total).
   * Already left out of the production totals. Optional, additive.
   */
  deletedServices: z
    .array(
      z.object({
        locationId: z.uuid(),
        count: z.number().int().nonnegative(),
        cents: z.number().int(),
      }),
    )
    .optional(),
});
export type DaySheet = z.infer<typeof DaySheetSchema>;

export const PRODUCTION_GROUPS = ['office', 'provider', 'day', 'month', 'code'] as const;

export const ProductionReportQuerySchema = z.object({
  from: IsoDateSchema,
  to: IsoDateSchema,
  groupBy: z.enum(PRODUCTION_GROUPS).default('office'),
  /** Omitted: the office's (else the practice's) default preset. */
  preset: MetricPresetSchema.optional(),
});
export type ProductionReportQuery = z.input<typeof ProductionReportQuerySchema>;

export const ProductionRowSchema = z.object({
  key: z.string(),
  label: z.string(),
  /** Gross production (completed procedure fees). */
  grossCents: z.number().int(),
  /** Write-offs and adjustments on the preset's net basis; `null` when the ledger is not synced. */
  deductionsCents: z.number().int().nullable(),
  netCents: z.number().int().nullable(),
  patientPaymentsCents: z.number().int().nullable(),
  insurancePaymentsCents: z.number().int().nullable(),
  collectionsCents: z.number().int().nullable(),
  /** Collections / production on the preset's basis; `null` when n/a. */
  collectionRate: z.number().nullable(),
  /** Completed procedures in the row (production summary by code or provider). */
  procedures: z.number().int(),
});
export type ProductionRow = z.infer<typeof ProductionRowSchema>;

export const ProductionReportSchema = z.object({
  from: IsoDateSchema,
  to: IsoDateSchema,
  groupBy: z.enum(PRODUCTION_GROUPS),
  preset: MetricPresetSchema,
  scope: ScopeInfoSchema,
  rows: z.array(ProductionRowSchema),
  total: ProductionRowSchema,
  providerRefs: z.array(ProviderRefSchema),
  /**
   * Offices whose ledger is not synced: their deductions, payments and
   * collections are left out of every row (unknown, not $0).
   */
  ledgerUnavailable: z.array(OfficeGapSchema),
});
export type ProductionReport = z.infer<typeof ProductionReportSchema>;

export const AGING_PAGE_SIZE_MAX = 500;
export const AgingQuerySchema = z.object({
  asOf: IsoDateSchema.optional(),
  /** 1-based page of guarantor rows, largest balance first (HA-11). */
  page: z.coerce.number().int().min(1).default(1),
  pageSize: z.coerce.number().int().min(1).max(AGING_PAGE_SIZE_MAX).default(100),
});

export const AgingBucketsSchema = z.object({
  b0to30: z.number().int(),
  b31to60: z.number().int(),
  b61to90: z.number().int(),
  b91plus: z.number().int(),
  total: z.number().int(),
  credit: z.number().int(),
});
export type AgingBuckets = z.infer<typeof AgingBucketsSchema>;

export const AgingReportSchema = z.object({
  asOf: IsoDateSchema,
  scope: ScopeInfoSchema,
  rows: z.array(
    AgingBucketsSchema.extend({
      guarantorId: z.uuid(),
      guarantorName: z.string(),
      locationId: z.uuid(),
      lastPayment: IsoDateSchema.nullable(),
    }),
  ),
  offices: z.array(AgingBucketsSchema.extend({ locationId: z.uuid() })),
  total: AgingBucketsSchema,
  /** Offices whose ledger is not synced: no aging is computed there (never from procedures alone). */
  unavailable: z.array(OfficeGapSchema),
  page: z.number().int(),
  pageSize: z.number().int(),
  /** Guarantor rows across all pages. */
  totalRows: z.number().int(),
});
export type AgingReport = z.infer<typeof AgingReportSchema>;

/* ------------------------------------------------------------------------ */
/* Referral report (new patients by referral source)                         */
/* ------------------------------------------------------------------------ */

export const ReferralReportQuerySchema = z.object({ from: IsoDateSchema, to: IsoDateSchema });

export const ReferralReportSchema = z.object({
  from: IsoDateSchema,
  to: IsoDateSchema,
  scope: ScopeInfoSchema,
  rows: z.array(
    z.object({
      label: z.string(),
      count: z.number().int(),
      offices: z.array(z.object({ locationId: z.uuid(), count: z.number().int() })),
      /**
       * W15 marketing ROI: completed production of these new patients from
       * the start of the range to today (any office in scope). Null when
       * money is hidden from the caller.
       */
      productionCents: z.number().int().nullable().optional(),
    }),
  ),
  total: z.number().int(),
  /** Production of every new patient in the range (null when money is hidden). */
  totalProductionCents: z.number().int().nullable().optional(),
  /** Offices without first-visit dates: their new patients cannot be counted. */
  unavailable: z.array(OfficeGapSchema),
});
export type ReferralReport = z.infer<typeof ReferralReportSchema>;

/* ------------------------------------------------------------------------ */
/* Fee outliers (Data hygiene)                                               */
/* ------------------------------------------------------------------------ */

export const FeeOutlierSchema = z.object({
  procedureId: z.uuid(),
  patientId: z.uuid(),
  patientName: z.string(),
  locationId: z.uuid(),
  code: z.string(),
  /** OD ProcStat: 1 planned, 2 complete. */
  status: z.number().int(),
  date: IsoDateSchema.nullable(),
  /** `null` when money is hidden. */
  feeCents: z.number().int().nullable(),
  medianCents: z.number().int().nullable(),
  samples: z.number().int(),
  rules: z.array(z.enum(['ten_x_median', 'over_cap'])),
  acknowledged: z.boolean(),
});
export type FeeOutlierRow = z.infer<typeof FeeOutlierSchema>;

export const FeeOutliersSchema = z.object({
  scope: ScopeInfoSchema,
  rows: z.array(FeeOutlierSchema),
  rules: z.object({
    medianMultiple: z.number(),
    minSamples: z.number().int(),
    capCents: z.number().int(),
  }),
});
export type FeeOutliers = z.infer<typeof FeeOutliersSchema>;

export const OutlierAckBodySchema = z.object({ note: z.string().trim().max(500).optional() });
export type OutlierAckBody = z.infer<typeof OutlierAckBodySchema>;
export const OutlierAckResultSchema = z.object({
  acknowledged: z.literal(true),
  feeCents: z.number().int(),
});

/* ------------------------------------------------------------------------ */
/* Goals                                                                     */
/* ------------------------------------------------------------------------ */

export const GoalsQuerySchema = z.object({ month: MonthSchema.optional() });

export const GoalsSchema = z.object({ month: MonthSchema, goals: z.array(OfficeGoalSchema) });
export type Goals = z.infer<typeof GoalsSchema>;

export const GoalPutSchema = z.object({
  locationId: z.uuid(),
  month: MonthSchema,
  /** Up to $100M a month. */
  productionGoalCents: z.number().int().min(0).max(10_000_000_000).nullable(),
  newPatientGoal: z.number().int().min(0).max(100_000).nullable(),
});
export type GoalPut = z.infer<typeof GoalPutSchema>;

/* ---------- goals v2: targets, clinical hours, preset, pacing ---------- */

export const GOAL_KEYS = [
  'production',
  'restorative_production',
  'hygiene_production',
  'collections',
  'appointments',
  'hygiene_appointments',
  'new_patients',
  'hygiene_reappointment_rate',
  'collection_rate',
] as const;
export const GoalKeySchema = z.enum(GOAL_KEYS);
export type GoalKey = z.infer<typeof GoalKeySchema>;

/** How each goal is entered: money in cents, counts, or a fraction (0.97). */
export const GOAL_KEY_INFO: Record<
  GoalKey,
  { label: string; kind: 'cents' | 'count' | 'rate'; cadence: 'week' | 'month' }
> = {
  production: { label: 'Production', kind: 'cents', cadence: 'month' },
  restorative_production: { label: 'Restorative production', kind: 'cents', cadence: 'month' },
  hygiene_production: { label: 'Hygiene production', kind: 'cents', cadence: 'month' },
  collections: { label: 'Collections', kind: 'cents', cadence: 'month' },
  appointments: { label: 'Appointments', kind: 'count', cadence: 'week' },
  hygiene_appointments: { label: 'Hygiene appointments', kind: 'count', cadence: 'week' },
  new_patients: { label: 'New patients', kind: 'count', cadence: 'month' },
  hygiene_reappointment_rate: { label: 'Hygiene re-appointment', kind: 'rate', cadence: 'month' },
  collection_rate: { label: 'Collection rate', kind: 'rate', cadence: 'month' },
};

export const GoalTargetSchema = z.object({
  /** null: the whole practice. */
  locationId: z.uuid().nullable(),
  /** Set for a provider goal. */
  providerId: z.uuid().nullable(),
  goalKey: GoalKeySchema,
  cadence: z.enum(['week', 'month']),
  /** null: a standing goal for every month without its own. */
  month: MonthSchema.nullable(),
  value: z.number().min(0).max(10_000_000_000),
});
export type GoalTarget = z.infer<typeof GoalTargetSchema>;

export const GoalTargetPutSchema = z.object({
  targets: z
    .array(GoalTargetSchema.extend({ value: z.number().min(0).max(10_000_000_000).nullable() }))
    .max(500),
});
export type GoalTargetPut = z.infer<typeof GoalTargetPutSchema>;

export const AnalyticsSettingSchema = z.object({
  /** null: the practice default. */
  locationId: z.uuid().nullable(),
  /** Definition preset used when a screen does not name one. */
  preset: MetricPresetSchema.nullable(),
  /** Clinical minutes per weekday (index 0 = Sunday), all providers together. */
  weekdayMinutes: z.array(z.number().int().min(0).max(86_400)).length(7).nullable(),
});
export type AnalyticsSetting = z.infer<typeof AnalyticsSettingSchema>;

export const GoalDaySchema = z.object({ date: IsoDateSchema, cents: z.number().int() });

export const OfficeDailyGoalsSchema = z.object({
  locationId: z.uuid(),
  /** What weighted the days (provider schedules, clinical hours, working days). */
  source: z.enum(['provider_schedules', 'weekday_weights', 'working_days']),
  days: z.array(GoalDaySchema),
});
export type OfficeDailyGoals = z.infer<typeof OfficeDailyGoalsSchema>;

export const GoalsV2Schema = z.object({
  month: MonthSchema,
  /** The preset analytics uses for this view when none is named. */
  preset: MetricPresetSchema,
  settings: z.array(AnalyticsSettingSchema),
  targets: z.array(GoalTargetSchema),
  /** Production / new patient goal per office after practice goals are shared out. */
  effective: z.array(OfficeGoalSchema),
  /** Daily production goals per office (money withheld = empty days). */
  daily: z.array(OfficeDailyGoalsSchema),
  /** Office providers that can carry a provider goal. */
  providers: z.array(z.object({ id: z.uuid(), name: z.string(), type: z.string() })),
  /**
   * The KPI alert rules with a threshold the owner can set: Velora's default,
   * the practice's own and each office's own (owner role test: alerts judged
   * against a hidden 90% / 98% rule).
   */
  alertThresholds: z
    .array(
      z.object({
        kind: z.string(),
        label: z.string(),
        unit: z.enum(['ratio', 'cents']),
        /** Which way trips the alert: below (collections) or above (the rest). */
        trips: z.enum(['below', 'above']),
        defaultValue: z.number(),
        practice: z.number().nullable(),
        offices: z.array(z.object({ locationId: z.uuid(), value: z.number().nullable() })),
      }),
    )
    .default([]),
  /** Stored result: when computed and whether a refresh is queued. */
  snapshot: SnapshotInfoSchema.optional(),
});
export type GoalsV2 = z.infer<typeof GoalsV2Schema>;

/* ------------------------------------------------------------------------ */
/* Payer contracts: PPO renegotiation pack and paid below contract           */
/* ------------------------------------------------------------------------ */

export const RenegotiationQuerySchema = z.object({
  /** Months of completed procedures to read (ending today). */
  months: z.coerce.number().int().min(1).max(36).default(12),
});
export type RenegotiationQuery = z.input<typeof RenegotiationQuerySchema>;

export const RenegotiationExportQuerySchema = RenegotiationQuerySchema.extend({
  format: z.enum(['csv', 'pdf']),
  /** One payer (`carrierId|scheduleId`); omitted: every payer. */
  payer: z.string().max(80).optional(),
});

export const RenegotiationCodeSchema = z.object({
  code: z.string(),
  description: z.string(),
  volume: z.number().int(),
  /** Average UCR and allowed fee per procedure done (office fees where set). */
  ucrFeeCents: z.number().int().nullable(),
  allowedFeeCents: z.number().int().nullable(),
  shareOfUcr: z.number().nullable(),
  /** volume x (UCR - allowed). */
  writeoffCents: z.number().int(),
});
export type RenegotiationCode = z.infer<typeof RenegotiationCodeSchema>;

export const RenegotiationPayerSchema = z.object({
  /** `carrierId|scheduleId`. */
  key: z.string(),
  carrierId: z.uuid(),
  carrierName: z.string(),
  scheduleId: z.uuid(),
  scheduleName: z.string(),
  scheduleKind: z.enum(['ppo', 'allowed', 'copay', 'discount']),
  /** The schedule's average allowed / UCR over every code with both fees. */
  scheduleShareOfUcr: z.number().nullable(),
  scheduleCodes: z.number().int(),
  /** Patients whose primary plan is on this carrier and schedule today. */
  patients: z.number().int(),
  plans: z.number().int(),
  volume: z.number().int(),
  /** Procedures without a UCR or allowed fee on file (left out of the money). */
  unpricedVolume: z.number().int(),
  ucrCents: z.number().int(),
  allowedCents: z.number().int(),
  writeoffCents: z.number().int(),
  /** allowed / UCR weighted by the work done. */
  volumeShareOfUcr: z.number().nullable(),
  /** Ranked by write-off (the screen shows the top 15; the CSV every code). */
  codes: z.array(RenegotiationCodeSchema),
  /** A draft request letter (the owner edits and sends it; Velora sends nothing). */
  letter: z.string().default(''),
});
export type RenegotiationPayer = z.infer<typeof RenegotiationPayerSchema>;

export const RenegotiationPackSchema = z.object({
  from: IsoDateSchema,
  to: IsoDateSchema,
  ucrSchedule: z.object({ id: z.uuid(), name: z.string() }).nullable(),
  payers: z.array(RenegotiationPayerSchema),
});
export type RenegotiationPack = z.infer<typeof RenegotiationPackSchema>;

export const BelowContractQuerySchema = z.object({
  months: z.coerce.number().int().min(1).max(24).default(6),
});

export const BelowContractSchema = z.object({
  from: IsoDateSchema,
  to: IsoDateSchema,
  /** A line is listed when the payer allowed at least this much under the contract. */
  toleranceCents: z.number().int(),
  lines: z.array(
    z.object({
      lineId: z.uuid(),
      claimId: z.uuid(),
      locationId: z.uuid(),
      patientId: z.uuid(),
      carrierId: z.uuid().nullable(),
      carrierName: z.string(),
      code: z.string(),
      serviceDate: IsoDateSchema.nullable(),
      receivedDate: IsoDateSchema,
      /** What the payer allowed on the remittance (paid plus patient portion). */
      payerAllowedCents: z.number().int(),
      /** The allowed fee in the plan's schedule for the code. */
      contractAllowedCents: z.number().int(),
      paidCents: z.number().int(),
      shortfallCents: z.number().int(),
      remarks: z.string().nullable(),
    }),
  ),
  /** Monthly roll-up by carrier (month of the remittance). */
  months: z.array(
    z.object({
      month: MonthSchema,
      carrierName: z.string(),
      lines: z.number().int(),
      shortfallCents: z.number().int(),
    }),
  ),
  totalShortfallCents: z.number().int(),
});
export type BelowContract = z.infer<typeof BelowContractSchema>;

export const BelowContractTasksBodySchema = z.object({
  lineIds: z.array(z.uuid()).min(1).max(200),
  months: z.number().int().min(1).max(24).default(6),
});
export const BelowContractTasksResultSchema = z.object({
  created: z.number().int(),
  existing: z.number().int(),
});

/** Goal suggestions from history (owner role test: an empty Goals grid left every "vs goal" blank). */
export const GoalSuggestQuerySchema = z.object({
  month: MonthSchema.optional(),
  basis: z.enum(['last_year', 'last_3_months']).default('last_year'),
  /** Growth on top of the basis, in percent (5 = +5%). */
  percent: z.coerce.number().min(-50).max(100).default(5),
});
export type GoalSuggestQuery = z.input<typeof GoalSuggestQuerySchema>;

export const GoalSuggestionsSchema = z.object({
  month: MonthSchema,
  basis: z.enum(['last_year', 'last_3_months']),
  percent: z.number(),
  offices: z.array(
    z.object({
      locationId: z.uuid(),
      /** What this office's suggestion is based on (last year falls back to 3 months). */
      basis: z.enum(['last_year', 'last_3_months']).nullable(),
      from: IsoDateSchema.nullable(),
      to: IsoDateSchema.nullable(),
      /** Monthly goals; money is null without money.view at the office. */
      restorativeProductionCents: z.number().int().nullable(),
      hygieneProductionCents: z.number().int().nullable(),
      collectionsCents: z.number().int().nullable(),
      newPatients: z.number().int().nullable(),
    }),
  ),
  /** Monthly production per provider (left out without money.view at every office in view). */
  providers: z.array(z.object({ providerId: z.uuid(), productionCents: z.number().int() })),
});
export type GoalSuggestions = z.infer<typeof GoalSuggestionsSchema>;

export const AnalyticsSettingPutSchema = z.object({
  settings: z.array(AnalyticsSettingSchema).max(100),
});
export type AnalyticsSettingPut = z.infer<typeof AnalyticsSettingPutSchema>;

/* ------------------------------------------------------------------------ */
/* Helpers shared by API and web                                             */
/* ------------------------------------------------------------------------ */

/** Units whose values are money (hidden without `money.view`). */
export function isMoneyUnit(unit: MetricUnitKey): boolean {
  return (
    unit === 'cents' ||
    unit === 'cents_per_hour' ||
    unit === 'cents_per_visit' ||
    unit === 'cents_per_patient' ||
    unit === 'cents_per_day'
  );
}

/* ------------------------------------------------------------------------ */
/* Metric series (any dictionary metric, bucket by bucket)                   */
/* ------------------------------------------------------------------------ */

export const SERIES_BUCKETS = ['day', 'week', 'month'] as const;
/** At most this many buckets per series (one compute per bucket and metric). */
export const SERIES_MAX_BUCKETS = 62;

export const MetricSeriesQuerySchema = z.object({
  /** Comma-separated metric ids (1 to 4). */
  metrics: z.string().regex(/^[a-z][a-z0-9_]*(,[a-z][a-z0-9_]*){0,3}$/),
  bucket: z.enum(SERIES_BUCKETS).default('month'),
  from: IsoDateSchema,
  to: IsoDateSchema,
  /** Split each bucket by this dimension (default: none, totals only). */
  dimension: PivotDimensionSchema.optional(),
  preset: MetricPresetSchema.optional(),
  /** Practice "today" override (tests, past views). */
  date: IsoDateSchema.optional(),
});
export type MetricSeriesQuery = z.input<typeof MetricSeriesQuerySchema>;

export const MetricSeriesBucketSchema = z.object({
  start: IsoDateSchema,
  end: IsoDateSchema,
  label: z.string(),
  total: z.record(z.string(), PivotCellSchema),
  groups: z.array(PivotGroupSchema),
});

export const MetricSeriesSchema = z.object({
  bucket: z.enum(SERIES_BUCKETS),
  dimension: PivotDimensionSchema.nullable(),
  preset: MetricPresetSchema,
  scope: ScopeInfoSchema,
  metrics: z.array(PivotMetricInfoSchema),
  buckets: z.array(MetricSeriesBucketSchema),
  officeGaps: z.array(OfficeGapSchema.extend({ metricId: z.string() })),
  notes: z.array(z.string()),
  generatedAt: z.string(),
});
export type MetricSeries = z.infer<typeof MetricSeriesSchema>;

/** Sections of Analytics > Practice health and the dictionary metrics each shows. */
export const PRACTICE_HEALTH_SECTIONS = {
  front_desk: [
    'speed_to_lead',
    'leads_reached_within_hour',
    'call_to_book_rate',
    'new_patient_call_conversion',
    'demand_turned_away',
    'text_response_time',
    'text_threads_unanswered',
    'previsit_ready_rate',
  ],
  cases: [
    'plan_acceptance_rate',
    'plan_case_acceptance_rate',
    'followup_close_rate',
    'plan_link_open_rate',
    'plan_read_minutes',
    'plan_opened_accept_rate',
  ],
  money: ['claim_days_to_pay', 'claim_denial_rate', 'writeoff_rate', 'collections'],
  growth: ['net_patient_growth', 'new_patient_cost', 'new_patient_value_12m', 'review_rating'],
  forecast: ['production_forecast_14d', 'production_forecast_30d'],
} as const;
export type PracticeHealthSection = keyof typeof PRACTICE_HEALTH_SECTIONS;

/** The headline metrics on the board, in display order. */
export const BOARD_HERO_METRICS = [
  'production_gross',
  'collections',
  'case_acceptance_dollars',
  'hygiene_reappointment_rate',
  'recall_due_booked_rate',
  'new_patients',
] as const;

/** Columns of the office comparison table, in display order. */
export const BOARD_TABLE_METRICS = [
  'production_gross',
  'production_net',
  'collections',
  'collection_rate',
  'case_acceptance_dollars',
  'hygiene_reappointment_rate',
  'recall_due_booked_rate',
  'new_patients',
  'patients_seen',
  'production_per_visit',
  'no_show_rate',
  'schedule_utilization',
  'unscheduled_treatment',
  'ar_past_due_share',
] as const;

/** Every metric the board computes (hero + table, de-duplicated). */
export const BOARD_METRICS: readonly string[] = [
  ...new Set<string>([...BOARD_HERO_METRICS, ...BOARD_TABLE_METRICS]),
];

/* ------------------------------------------------------------------------ */
/* Office comparison (Analytics -> Compare)                                  */
/* ------------------------------------------------------------------------ */

/** Periods of the office comparison; `custom` takes `from` and `to`. */
export const COMPARE_PERIODS = ['mtd', 'last_month', 'qtd', 'ytd', '12m', 'custom'] as const;
export const ComparePeriodSchema = z.enum(COMPARE_PERIODS);
export type ComparePeriodKey = z.infer<typeof ComparePeriodSchema>;

export const COMPARE_PERIOD_LABELS: Record<ComparePeriodKey, string> = {
  mtd: 'Month to date',
  last_month: 'Last month',
  qtd: 'Quarter to date',
  ytd: 'Year to date',
  '12m': 'Last 12 months',
  custom: 'Custom',
};

/** Ways to put offices of different size side by side. */
export const COMPARE_BASES = ['total', 'per_day', 'per_provider'] as const;
export const CompareBasisSchema = z.enum(COMPARE_BASES);
export type CompareBasis = z.infer<typeof CompareBasisSchema>;

export const CompareQuerySchema = z.object({
  period: ComparePeriodSchema.default('mtd'),
  /** Custom period, inclusive (both required with `period=custom`). */
  from: IsoDateSchema.optional(),
  to: IsoDateSchema.optional(),
  compare: CompareModeSchema.default('prior'),
  /** Omitted: the office's (else the practice's) default preset. */
  preset: MetricPresetSchema.optional(),
  /** Include the 12-month trend per office (the small multiples). Off for the league table. */
  trend: z.enum(['true', 'false']).optional(),
  date: IsoDateSchema.optional(),
});
export type CompareQuery = z.input<typeof CompareQuerySchema>;

export const COMPARE_GROUPS = [
  'production',
  'collections',
  'patients',
  'treatment',
  'schedule',
  'ar',
] as const;
export const CompareGroupSchema = z.enum(COMPARE_GROUPS);
export type CompareGroup = z.infer<typeof CompareGroupSchema>;

export const COMPARE_GROUP_LABELS: Record<CompareGroup, string> = {
  production: 'Production',
  collections: 'Collections',
  patients: 'Patients & hygiene',
  treatment: 'Treatment',
  schedule: 'Schedule',
  ar: 'Accounts receivable',
};

/**
 * Why an office has no number for a KPI. `not_synced`: its PMS link does not
 * send a feed the KPI needs (reason given); `hidden`: money the viewer may
 * not see; `no_data`: nothing to divide (no visits, no presented treatment,
 * period shorter than the KPI allows). Never shown as 0, never ranked.
 */
export const CompareStateSchema = z.enum(['ok', 'not_synced', 'hidden', 'no_data']);
export type CompareState = z.infer<typeof CompareStateSchema>;

export const CompareOfficeStatSchema = z.object({
  locationId: z.uuid(),
  value: z.number().nullable(),
  /** Same office, comparison period, same basis. */
  compare: z.number().nullable(),
  /** 1 = best. Ties share a rank. Null: not ranked (n/a, or the KPI has no better direction). */
  rank: z.number().int().nullable(),
  best: z.boolean(),
  worst: z.boolean(),
  /** Distance to the best office in the KPI's unit (0 for the best). */
  gapToBest: z.number().nullable(),
  /** Trailing 12 months, oldest first (null: n/a that month). Empty unless `trend=true`. */
  trend: z.array(z.number().nullable()),
});
export type CompareOfficeStat = z.infer<typeof CompareOfficeStatSchema>;

export const CompareStatsSchema = z.object({
  offices: z.array(CompareOfficeStatSchema),
  /** Plain average of the offices that have a number (n/a excluded). */
  average: z.number().nullable(),
  compareAverage: z.number().nullable(),
  best: z.number().nullable(),
  /** Offices that took part in the ranking. */
  ranked: z.number().int(),
});
export type CompareStats = z.infer<typeof CompareStatsSchema>;

export const CompareKpiSchema = z.object({
  id: z.string(),
  name: z.string(),
  short: z.string(),
  group: CompareGroupSchema,
  /** Unit of the `total` basis; normalised bases divide it by days or providers. */
  unit: MetricUnitSchema,
  better: z.enum(['higher', 'lower', 'neutral']),
  money: z.boolean(),
  /** Metric Dictionary entry the number comes from (drill-down, formula). */
  metricId: z.string(),
  formula: z.string(),
  /** Additive flow KPIs divide by days worked / producing providers; others are not normalised. */
  normalizable: z.boolean(),
  status: z.array(
    z.object({
      locationId: z.uuid(),
      state: CompareStateSchema,
      unavailable: MetricGapSchema.optional(),
      partial: z.array(MetricGapSchema).optional(),
    }),
  ),
  bases: z.object({
    total: CompareStatsSchema,
    per_day: CompareStatsSchema.nullable(),
    per_provider: CompareStatsSchema.nullable(),
  }),
});
export type CompareKpi = z.infer<typeof CompareKpiSchema>;

export const CompareOfficeSchema = z.object({
  locationId: z.uuid(),
  /** Distinct dates with completed production in the period (per-day divisor). */
  daysWorked: z.number().int(),
  compareDaysWorked: z.number().int(),
  /** Distinct rendering providers with completed procedures in the period (per-provider divisor). */
  providers: z.number().int(),
  compareProviders: z.number().int(),
});
export type CompareOffice = z.infer<typeof CompareOfficeSchema>;

export const OfficeComparisonSchema = z.object({
  period: ResolvedPeriodSchema,
  comparePeriod: ResolvedPeriodSchema,
  preset: MetricPresetSchema,
  scope: ScopeInfoSchema,
  offices: z.array(CompareOfficeSchema),
  /** Trend month keys (`YYYY-MM`), oldest first; the last may be partial. Empty unless `trend=true`. */
  months: z.array(MonthSchema),
  kpis: z.array(CompareKpiSchema),
  generatedAt: z.string(),
});
export type OfficeComparison = z.infer<typeof OfficeComparisonSchema>;

/* ------------------------------------------------------------------------ */
/* Schedule analysis (Month > Week > Day, production vs goal)                */
/* ------------------------------------------------------------------------ */

export const ScheduleAnalysisQuerySchema = z.object({
  month: MonthSchema.optional(),
  /** Practice-local "today" override (tests, history). */
  date: IsoDateSchema.optional(),
});
export type ScheduleAnalysisQuery = z.input<typeof ScheduleAnalysisQuerySchema>;

/** Production against goal for a day, week, month or provider. Money is null when hidden or unknown. */
export const ScheduleAmountsSchema = z.object({
  /** Completed production before today, plus scheduled production for today and later. */
  productionCents: z.number().int().nullable(),
  completedCents: z.number().int().nullable(),
  scheduledCents: z.number().int().nullable(),
  goalCents: z.number().int().nullable(),
  /** production - goal; null without a goal. */
  diffCents: z.number().int().nullable(),
  appointments: z.number().int(),
});
export type ScheduleAmounts = z.infer<typeof ScheduleAmountsSchema>;

export const ScheduleProviderDaySchema = ScheduleAmountsSchema.extend({
  providerId: z.string(),
  availableMinutes: z.number().int(),
});

export const ScheduleDaySchema = ScheduleAmountsSchema.extend({
  date: IsoDateSchema,
  /** past = completed only; today and future add what is scheduled. */
  kind: z.enum(['past', 'today', 'future']),
  providers: z.array(ScheduleProviderDaySchema),
});
export type ScheduleDay = z.infer<typeof ScheduleDaySchema>;

export const ScheduleWeekSchema = ScheduleAmountsSchema.extend({
  /** Monday..Sunday, clipped to the month. */
  start: IsoDateSchema,
  end: IsoDateSchema,
  days: z.array(ScheduleDaySchema),
});
export type ScheduleWeek = z.infer<typeof ScheduleWeekSchema>;

export const ScheduleOfficeSchema = ScheduleAmountsSchema.extend({
  locationId: z.uuid(),
  /** Why scheduled production is unknown (no procedure link and no PMS amount). */
  scheduledUnavailable: MetricGapSchema.nullable(),
  goalSource: z.enum(['provider_schedules', 'weekday_hours', 'working_days', 'none']),
  weeks: z.array(ScheduleWeekSchema),
});
export type ScheduleOffice = z.infer<typeof ScheduleOfficeSchema>;

export const ScheduleAnalysisSchema = z.object({
  month: MonthSchema,
  today: IsoDateSchema,
  scope: ScopeInfoSchema,
  offices: z.array(ScheduleOfficeSchema),
  /** Every office in view added up, by week and day (providers merged). */
  total: ScheduleAmountsSchema.extend({ weeks: z.array(ScheduleWeekSchema) }),
  providers: z.array(ProviderRefSchema),
  generatedAt: z.string(),
  /** Stored result: when computed and whether a refresh is queued. */
  snapshot: SnapshotInfoSchema.optional(),
});
export type ScheduleAnalysis = z.infer<typeof ScheduleAnalysisSchema>;

/* ------------------------------------------------------------------------ */
/* Unscheduled treatment aging                                               */
/* ------------------------------------------------------------------------ */

export const UNSCHEDULED_AGE_BUCKETS = [
  { key: 'm0_6', label: '0–6 months', fromMonths: 0, toMonths: 6 },
  { key: 'm6_9', label: '6–9 months', fromMonths: 6, toMonths: 9 },
  { key: 'm9_12', label: '9–12 months', fromMonths: 9, toMonths: 12 },
  { key: 'm12_18', label: '12–18 months', fromMonths: 12, toMonths: 18 },
  { key: 'm18_36', label: '18–36 months', fromMonths: 18, toMonths: 36 },
  { key: 'over_36', label: 'Over 36 months', fromMonths: 36, toMonths: null },
  { key: 'unknown', label: 'No plan date', fromMonths: null, toMonths: null },
] as const;
export type UnscheduledAgeBucketKey = (typeof UNSCHEDULED_AGE_BUCKETS)[number]['key'];

export const UnscheduledAgeBucketSchema = z.object({
  key: z.enum(['m0_6', 'm6_9', 'm9_12', 'm12_18', 'm18_36', 'over_36', 'unknown']),
  label: z.string(),
  /** Patients whose oldest unscheduled line falls in the bucket. */
  patients: z.number().int(),
  /** Unscheduled lines planned in the bucket. */
  items: z.number().int(),
  /** Fees of those lines; null when money is hidden. */
  cents: z.number().int().nullable(),
});
export type UnscheduledAgeBucket = z.infer<typeof UnscheduledAgeBucketSchema>;

export const UnscheduledAgingQuerySchema = z.object({ asOf: IsoDateSchema.optional() });

export const UnscheduledAgingSchema = z.object({
  asOf: IsoDateSchema,
  scope: ScopeInfoSchema,
  offices: z.array(
    z.object({ locationId: z.uuid(), buckets: z.array(UnscheduledAgeBucketSchema) }),
  ),
  total: z.array(UnscheduledAgeBucketSchema),
  /** Offices whose figure still counts booked lines (no procedure-appointment link). */
  partialOffices: z.array(z.uuid()),
});
export type UnscheduledAging = z.infer<typeof UnscheduledAgingSchema>;

/* ------------------------------------------------------------------------ */
/* Emailed reports (per-user opt-in, off by default)                         */
/* ------------------------------------------------------------------------ */

export const ReportSubscriptionsSchema = z.object({
  daily: z.boolean(),
  weekly: z.boolean(),
  monthly: z.boolean(),
});
export type ReportSubscriptions = z.infer<typeof ReportSubscriptionsSchema>;

export const ReportSubscriptionsPutSchema = ReportSubscriptionsSchema;
export type ReportSubscriptionsPut = z.infer<typeof ReportSubscriptionsPutSchema>;

/* ------------------------------------------------------------------------ */
/* Scorecard (DI-style: chosen KPIs by office, totals row, 6-month trend)    */
/* ------------------------------------------------------------------------ */

/** KPIs shown when the viewer has not chosen their own. */
export const SCORECARD_DEFAULT_METRICS = [
  'production_gross',
  'collections',
  'collection_rate',
  'new_patients',
  'visits',
  'hygiene_reappointment_rate',
  'preappointment_rate',
  'case_acceptance_dollars',
  'same_day_acceptance_rate',
  'net_growth_rate',
  'annual_patient_value',
  'ar_total',
  'ar_days',
] as const;

export const KpiScorecardQuerySchema = z.object({
  /** Comma-separated metric ids (dictionary ids); omitted: the default set. */
  metrics: z.string().max(2000).optional(),
  /** Month shown in the value column (month to date when it is the current month). */
  month: MonthSchema.optional(),
  preset: MetricPresetSchema.optional(),
  date: IsoDateSchema.optional(),
});
export type KpiScorecardQuery = z.input<typeof KpiScorecardQuerySchema>;

export const KpiScorecardCellSchema = MetricCellSchema.extend({
  /** Monthly values, oldest first (the last is the selected month); null = n/a or hidden. */
  trend: z.array(z.number().nullable()),
  /** Goal for the selected month in the metric's unit; null without a goal. */
  goal: z.number().nullable(),
  /** Goal per trend month (the goal line). */
  goalTrend: z.array(z.number().nullable()),
});
export type KpiScorecardCell = z.infer<typeof KpiScorecardCellSchema>;

export const KpiScorecardRowSchema = z.object({
  metricId: z.string(),
  name: z.string(),
  unit: MetricUnitSchema,
  better: z.enum(['higher', 'lower', 'neutral']),
  money: z.boolean(),
  formula: z.string(),
  offices: z.array(
    KpiScorecardCellSchema.extend({
      locationId: z.uuid(),
      /** Where the office sits among the practice's own offices (WP-16). */
      band: z
        .enum(['best', 'above_median', 'below_median', 'weakest', 'only'])
        .nullable()
        .optional(),
    }),
  ),
  /** The location totals row. */
  total: KpiScorecardCellSchema,
  /** Best / median / weakest of this practice's own offices (never other practices). */
  band: z
    .object({
      offices: z.number().int(),
      best: z.number(),
      median: z.number(),
      weakest: z.number(),
    })
    .nullable()
    .optional(),
});
export type KpiScorecardRow = z.infer<typeof KpiScorecardRowSchema>;

export const KpiScorecardSchema = z.object({
  month: MonthSchema,
  /** The trend months, oldest first. */
  months: z.array(MonthSchema),
  preset: MetricPresetSchema,
  scope: ScopeInfoSchema,
  rows: z.array(KpiScorecardRowSchema),
  /** Every metric that can be added (for the customise menu). */
  catalog: z.array(z.object({ id: z.string(), name: z.string(), category: z.string() })),
  generatedAt: z.string(),
  /** Stored result: when computed and whether a refresh is queued. */
  snapshot: SnapshotInfoSchema.optional(),
});
export type KpiScorecard = z.infer<typeof KpiScorecardSchema>;

/* ------------------------------------------------------------------------ */
/* Team task results (DI follow-up results)                                  */
/* ------------------------------------------------------------------------ */

export const TaskResultsQuerySchema = z.object({
  /** Tasks created from..to (practice-local dates). */
  from: IsoDateSchema,
  to: IsoDateSchema,
});
export type TaskResultsQuery = z.input<typeof TaskResultsQuerySchema>;

export const TaskResultRowSchema = z.object({
  /** Assigned team member; null = unassigned. */
  userId: z.uuid().nullable(),
  name: z.string(),
  tasks: z.number().int(),
  done: z.number().int(),
  /** Outbound calls, texts and emails to the task's patient after the task was created. */
  attempts: z.number().int(),
  /** Tasks whose patient answered (an inbound message or call, or a call marked contacted). */
  contacted: z.number().int(),
  contactedRate: z.number().nullable(),
  /** Production of appointments booked for the task's patients after the task. */
  scheduledCents: z.number().int().nullable(),
  /** Patient payments posted by the family after the task. */
  collectedCents: z.number().int().nullable(),
});
export type TaskResultRow = z.infer<typeof TaskResultRowSchema>;

export const TaskResultsSchema = z.object({
  from: IsoDateSchema,
  to: IsoDateSchema,
  scope: ScopeInfoSchema,
  rows: z.array(TaskResultRowSchema),
  total: TaskResultRowSchema,
});
export type TaskResults = z.infer<typeof TaskResultsSchema>;

/* ------------------------------------------------------------------------ */
/* PPO analysis: discount vs UCR by code category, patients per plan          */
/* ------------------------------------------------------------------------ */

/**
 * PPO analysis (Marketplace `ppo_analysis`; owner feedback 2026-10-28, Dr.
 * John: "across the board, how much of a discount am I taking with Delta vs
 * my UCR fees", by CDT category and overall, weighted by the office's own
 * procedure mix, with the patients on each plan, to decide whether to stay
 * in or drop a PPO). Aggregates only: codes, schedules, carriers, counts and
 * money; never a patient.
 */
export const PpoAnalysisQuerySchema = z.object({
  /** Months of completed procedures to weight by (ending today). */
  months: z.coerce.number().int().min(3).max(36).default(12),
  /** Compare against this schedule instead of each office's own UCR schedule. */
  ucr: z.uuid().optional(),
});
export type PpoAnalysisQuery = z.input<typeof PpoAnalysisQuerySchema>;

/** One CDT category's figures (`series` from `@velora/domain/codes` cdtSeriesOf). */
export const PpoCategorySchema = z.object({
  series: z.string(),
  /** Procedures with both fees on file. */
  pricedVolume: z.number().int(),
  unpricedVolume: z.number().int(),
  ucrCents: z.number().int(),
  allowedCents: z.number().int(),
  writeoffCents: z.number().int(),
  /** 1 - allowed / UCR over the priced work; null without priced work. */
  discount: z.number().nullable(),
});
export type PpoCategory = z.infer<typeof PpoCategorySchema>;

export const PpoTotalsSchema = PpoCategorySchema.omit({ series: true }).extend({
  byCategory: z.array(PpoCategorySchema),
});
export type PpoTotals = z.infer<typeof PpoTotalsSchema>;

export const PpoPlanRowSchema = z.object({
  /** `locationId|scheduleId`. */
  key: z.string(),
  locationId: z.uuid().nullable(),
  scheduleId: z.uuid(),
  scheduleName: z.string(),
  /** The PMS's fee schedule number ("3" in Dentrix), when synced. */
  schedulePmsId: z.string().nullable(),
  /** The plans point at the office's UCR schedule: billed at the full fee (no discount). */
  atUcr: z.boolean(),
  /** Insurance plans that use the schedule. */
  plans: z.number().int(),
  /**
   * Carriers behind those plans by name (a PMS often keeps one carrier row
   * per employer group: "Delta Dental of MI" many times), most patients first.
   */
  carriers: z.array(z.object({ name: z.string(), patients: z.number().int() })),
  /** Active patients whose primary plan uses the schedule today. */
  patients: z.number().int(),
  /** Of those, how many had a completed procedure in the window. */
  patientsSeen: z.number().int(),
  /** Codes with a fee on both schedules (the schedule-level comparison). */
  codesCompared: z.number().int(),
  /** The schedule priced at the office's whole procedure mix (same work for every plan). */
  mix: PpoTotalsSchema,
  /** The work actually done for the plan's members (primary plan on the day). */
  members: PpoTotalsSchema,
});
export type PpoPlanRow = z.infer<typeof PpoPlanRowSchema>;

export const PpoAnalysisSchema = z.object({
  from: IsoDateSchema,
  to: IsoDateSchema,
  months: z.number().int(),
  offices: z.array(
    z.object({
      locationId: z.uuid(),
      /** The schedule this office's fees are compared with; null: none found. */
      ucrSchedule: z.object({ id: z.uuid(), name: z.string() }).nullable(),
      /** Why that schedule: kind UCR, the PMS's default schedule (#1), the practice default, or chosen. */
      ucrSource: z.enum(['ucr_kind', 'pms_default', 'practice', 'chosen', 'none']),
      /** Completed procedures in the window (the procedure mix). */
      procedures: z.number().int(),
      /** Active patients with any primary plan, and with none. */
      insuredPatients: z.number().int(),
      uninsuredPatients: z.number().int(),
    }),
  ),
  /**
   * Active patients by primary carrier name across every plan ("how many
   * patients have MetLife"), most first.
   */
  carriers: z.array(
    z.object({
      name: z.string(),
      patients: z.number().int(),
      /** Fee schedules those patients' plans use. */
      schedules: z.number().int(),
    }),
  ),
  /** Discounted plans (not at UCR) rolled up: the across-the-board answer. */
  overall: PpoTotalsSchema,
  plans: z.array(PpoPlanRowSchema),
  /** Plain-words notes on what the figures leave out. */
  notes: z.array(z.string()),
});
export type PpoAnalysis = z.infer<typeof PpoAnalysisSchema>;

/* ------------------------------------------------------------------------ */
/* Money you can recover (roadmap Tier 1 #1-6)                               */
/* ------------------------------------------------------------------------ */

/**
 * One recoverable-money metric over the scope: the engine's value for the
 * whole office set plus each office's (a cell per office, "not synced" with
 * the reason where the office cannot supply it). Cents are null without
 * money.view.
 */
export const RecoverableMetricSchema = z.object({
  metricId: z.string(),
  name: z.string(),
  unit: MetricUnitSchema,
  total: MetricCellSchema,
  offices: z.array(MetricCellSchema.extend({ locationId: z.uuid() })),
  coverage: CoverageSchema,
});
export type RecoverableMetric = z.infer<typeof RecoverableMetricSchema>;

export const RecoverableMonthSchema = z.object({
  /** YYYY-MM. */
  month: z.string(),
  from: z.iso.date(),
  to: z.iso.date(),
  metric: RecoverableMetricSchema,
});
export type RecoverableMonth = z.infer<typeof RecoverableMonthSchema>;

/** A breakdown row (weekday, hour, lead time, age bucket, origin ...). */
export const RecoverableSliceSchema = z.object({
  key: z.string(),
  label: z.string(),
  count: z.number().int(),
  cents: z.number().int().nullable(),
  /** Openings: how many of `count` were filled. */
  filled: z.number().int().nullable().default(null),
});
export type RecoverableSlice = z.infer<typeof RecoverableSliceSchema>;

export const RecoverableMoneySchema = z.object({
  asOf: z.iso.date(),
  /** The flow window (missed calls, openings, missed appointments): three calendar months to asOf. */
  window: z.object({ from: z.iso.date(), to: z.iso.date() }),
  months: z.array(z.string()),
  missedCalls: z.object({
    window: RecoverableMetricSchema,
    monthly: z.array(RecoverableMonthSchema),
    /** Lost callers whose 14 days to book are not over yet. */
    pendingCallers: z.number().int(),
    offices: z.array(
      z.object({
        locationId: z.uuid(),
        /** First call Velora holds for the office (months before it read $0 for want of data). */
        callsFrom: z.iso.date().nullable(),
        newPatientCents: z.number().int().nullable(),
        visitCents: z.number().int().nullable(),
      }),
    ),
    byHour: z.array(RecoverableSliceSchema),
  }),
  decay: z.object({
    buckets: z.array(
      z.object({
        key: z.string(),
        label: z.string(),
        amount: RecoverableMetricSchema,
        conversion: RecoverableMetricSchema,
        /** Dollars in the bucket x its historical conversion (null when either is n/a). */
        expectedCents: z.number().int().nullable(),
      }),
    ),
    /** Unscheduled treatment the PMS sent without a plan date (in no bucket). */
    undatedCents: z.number().int().nullable(),
    totalCents: z.number().int().nullable(),
  }),
  benefits: z.object({
    metric: RecoverableMetricSchema,
    resetWithinDays: z.number().int(),
    /** Active patients with unscheduled treatment looked at. */
    candidates: z.number().int(),
    noEstimate: z.number().int(),
    /** Left out: low-confidence estimates (as the campaign builder's default). */
    lowConfidence: z.number().int(),
    /** Left out: plans whose annual maximum is a placeholder for "no maximum". */
    noRealMax: z.number().int(),
    /** Offices with plans but no claims this year in Velora: benefits used are not subtracted there. */
    noClaimsOffices: z.array(z.uuid()),
  }),
  recall: z.object({
    patients: RecoverableMetricSchema,
    value: RecoverableMetricSchema,
    /** Months since the last hygiene visit. */
    buckets: z.array(RecoverableSliceSchema),
    /** How many had a hygiene visit on file (the rest were dated from their recall). */
    lastVisitKnown: z.number().int(),
  }),
  openings: z.object({
    fillRate: RecoverableMetricSchema,
    recovered: RecoverableMetricSchema,
    monthly: z.array(
      z.object({
        month: z.string(),
        fillRate: RecoverableMetricSchema,
        recovered: RecoverableMetricSchema,
      }),
    ),
    byOrigin: z.array(RecoverableSliceSchema),
  }),
  noShows: z.object({
    window: RecoverableMetricSchema,
    monthly: z.array(RecoverableMonthSchema),
    byWeekday: z.array(RecoverableSliceSchema),
    byHour: z.array(RecoverableSliceSchema),
    byLead: z.array(RecoverableSliceSchema),
    /** Patients by how many times they missed in the window (1, 2, 3+). */
    byRepeat: z.array(RecoverableSliceSchema),
  }),
  notes: z.array(z.string()),
  scope: ScopeInfoSchema,
  generatedAt: z.string(),
  snapshot: SnapshotInfoSchema.optional(),
});
export type RecoverableMoney = z.infer<typeof RecoverableMoneySchema>;

/** The year-end benefits campaign list (CSV export). */
export const RecoverableBenefitsExportQuerySchema = z.object({
  locationId: z.uuid().optional(),
});
